import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  AddCircleOutline as ExpandIcon,
  CheckCircleOutline as ValidateIcon,
  DateRange as DateIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  EditOutlined as EditIcon,
  PlaylistAddCheck as ValidateAllIcon,
  RemoveCircleOutline as CollapseIcon,
  RestartAlt as ResetIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Rule as RuleIcon,
  LocationOn as LocationOnIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ChatBubbleOutline as CommentNoteIcon,
  ReceiptLong as ReceiptLongIcon,
  Handyman as HandymanIcon,
  AccessTime as AccessTimeIcon,
  WbSunnyOutlined as SunIcon,
  WbTwilight as TwilightIcon,
  NightsStayOutlined as MoonIcon,
  ShieldOutlined as ShieldIcon,
} from '@mui/icons-material';
import { fetchExtraTypeConfigList } from '../../../AfterSalesExtra/Domain/Services/ExtraTypeConfigService';
import { deleteAfterSalesExtra } from '../../../AfterSalesExtra/Domain/Services/DeleteAfterSalesExtraService';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import {
  EXTRAS_MANTENIMIENTO,
  EXTRAS_EMPLOYE,
  EXTRAS_VALIDATOR,
  EXTRAS_REPARACIONES_EMPLEADO,
  EXTRAS_REPARACIONES_VALIDADOR,
} from 'Shared/Security/Application/Constants/PermissionsConstant';
import { getUsersList } from 'User/Domain/Services/GetUsersListService';
import { getAfterSalesExtraList } from '../../../AfterSalesExtra/Domain/Services/GetAfterSalesExtraListService';
import { patchAfterSalesExtra } from '../../../AfterSalesExtra/Domain/Services/PatchAfterSalesExtraService';
import { postAfterSalesExtra } from '../../../AfterSalesExtra/Domain/Services/PostAfterSalesExtraService';
import { getRepairExtraList } from '../../../RepairExtra/Domain/Services/GetRepairExtraListService';
import { patchRepairExtra } from '../../../RepairExtra/Domain/Services/PatchRepairExtraService';
import { getDelegationList } from 'Delegation/Domain/Services/GetDelegationListService';
import { getExtrasSummary } from '../../Domain/Services/GetExtrasSummaryService';
import { getExtrasFilterOptions } from '../../Domain/Services/GetExtrasFilterOptionsService';
import { patchValidationExtra } from '../../Domain/Services/PatchValidationExtraService';
import { fetchOrgChart } from 'OrgChart/Domain/Services/GetOrgChartService';
import { exportExtrasMatrix } from '../../../AfterSalesExtra/Domain/Services/ExportExtrasMatrixService';
import { getGoogleMapsEmbedUrl, getGoogleMapsExternalUrl } from 'Shared/Config/GoogleMapsConfig';

const DEFAULT_SUMMARY = {
  total: 0,
  totalTheoretical: 0,
  totalFinal: 0,
  trimmedHoursTheoretical: 0,
  trimmedHoursFinal: 0,
  fixedAmountTheoretical: 0,
  fixedAmountFinal: 0,
  byType: [],
};

const renderDetailValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const strVal = String(value).trim().toLowerCase();
  if (strVal === 'sí' || strVal === 'si' || strVal === 'true') {
    return (
      <Tooltip title="Sí" arrow>
        <CheckIcon sx={{ color: '#16a34a', fontSize: 19, verticalAlign: 'middle' }} />
      </Tooltip>
    );
  }
  if (strVal === 'no' || strVal === 'false') {
    return (
      <Tooltip title="No" arrow>
        <CloseIcon sx={{ color: '#94a3b8', fontSize: 18, verticalAlign: 'middle' }} />
      </Tooltip>
    );
  }
  return value;
};

const DEFAULT_FILTERS = {
  search: '',
  extraType: 'all',
  repairExtraType: 'all',
  shiftType: 'all',
  delegation: 'all',
  employeeSearch: [],
  validatorSearch: [],
  status: 'all',
  incidence: 'all',
  extraDateFrom: '',
  extraDateTo: '',
  paymentDateFrom: '',
  paymentDateTo: '',
};

const REPAIR_EXTRA_TYPE_OPTIONS = [
  { value: 'all', label: 'Todos los tipos' },
  { value: 'horas_recortadas', label: 'Por horas recortadas' },
  { value: 'fuera_de_horas', label: 'Fuera de horas (importe fijo acordado)' },
];

const SHIFT_OPTIONS = [
  { value: 'all', label: 'Todos los turnos' },
  { value: 'MANANA', label: '☀️ Mañana' },
  { value: 'TARDE', label: '🌆 Tarde' },
  { value: 'NOCHE', label: '🌙 Noche' },
  { value: 'GUARDIA', label: '🛡️ Guardia' },
  { value: 'none', label: 'Sin turno asignado' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todo' },
  { value: 'pending_mine', label: 'Pendiente validar por mí' },
  { value: 'pending_hr', label: 'Pendiente Dept. Personas' },
  { value: 'pending_hr_out_of_date', label: 'Pendiente Dept. Personas (Fuera de fecha / Mes siguiente)' },
  { value: 'pending_employee', label: 'Pendiente empleado' },
  { value: 'pending_responsible', label: 'Pendiente de responsable' },
  { value: 'validated', label: 'Validado' },
];

const INCIDENCE_OPTIONS = [
  { value: 'all', label: 'Todo' },
  { value: 'yes', label: 'Sí' },
  { value: 'no', label: 'No' },
];

const HUMAN_RESOURCES_STRUCTURE_NAME = 'RECURSOS HUMANOS';

function formatCurrency(value, decimalsOrIsRepairs = 0) {
  const amount = Number(value ?? 0);
  const decimals = typeof decimalsOrIsRepairs === 'boolean'
    ? (decimalsOrIsRepairs ? 2 : 0)
    : Number(decimalsOrIsRepairs || 0);

  if (decimals > 0) {
    return `${amount.toFixed(decimals)}€`;
  }
  return `${Math.round(amount)}€`;
}

function formatEmployeeShortName(row) {
  if (row?.employeeShortName) {
    return row.employeeShortName;
  }
  const fullName = (row?.employeeName || '').trim();
  if (!fullName) return '-';
  const parts = fullName.split(/\s+/);
  if (parts.length <= 2) return fullName;
  return `${parts[0]} ${parts[1]}`;
}

function getEmployeeInitials(row) {
  if (row?.employeeInitials) {
    return row.employeeInitials;
  }
  const shortName = (row?.employeeShortName || formatEmployeeShortName(row) || '').trim();
  if (!shortName || shortName === '-') {
    return '?';
  }
  const parts = shortName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return shortName.substring(0, 2).toUpperCase();
}

function formatWorkOrder(workOrder) {
  if (!workOrder) {
    return '-';
  }
  const trimmed = String(workOrder).trim();
  return trimmed.replace(/^0+/, '') || '0';
}

function formatDate(value, withTime = false) {
  if (!value) {
    return '-';
  }

  const d = dayjs(value);
  if (!d.isValid()) {
    return '-';
  }

  return withTime ? d.format('DD/MM/YY HH:mm') : d.format('DD/MM/YY');
}

function formatDateRangeLabel(rangeFrom, rangeTo) {
  if (!rangeFrom && !rangeTo) {
    return '';
  }

  if (rangeFrom && rangeTo) {
    return `${formatDate(rangeFrom)} - ${formatDate(rangeTo)}`;
  }

  if (rangeFrom) {
    return `${formatDate(rangeFrom)} -`;
  }

  return `- ${formatDate(rangeTo)}`;
}

function getStatusPresentation(row) {
  const fullLabel = row.statusLabel || '-';

  if (row.status === 'error') {
    return {
      shortLabel: 'Error',
      fullLabel,
      color: 'error',
      variant: 'filled',
      bgColor: '#fef2f2',
      textColor: '#b91c1c',
      borderColor: '#fecaca',
    };
  }

  if (row.status === 'validated') {
    return {
      shortLabel: 'Validado',
      fullLabel,
      color: 'success',
      variant: 'filled',
      bgColor: '#ecfdf5',
      textColor: '#047857',
      borderColor: '#a7f3d0',
    };
  }

  if (row.status === 'pending_mine') {
    return {
      shortLabel: 'Pendiente (tu)',
      fullLabel,
      color: 'warning',
      variant: 'filled',
      bgColor: '#eff6ff',
      textColor: '#1d4ed8',
      borderColor: '#bfdbfe',
    };
  }

  if (row.status === 'pending_hr') {
    return {
      shortLabel: 'Pendiente Dept. Personas',
      fullLabel,
      color: 'secondary',
      variant: 'outlined',
      bgColor: '#f0f9ff',
      textColor: '#0369a1',
      borderColor: '#bae6fd',
    };
  }

  if (row.status === 'pending_hr_out_of_date') {
    return {
      shortLabel: 'Pendiente Dept. Personas (Fuera de fecha)',
      fullLabel,
      color: 'warning',
      variant: 'outlined',
      bgColor: '#fffbeb',
      textColor: '#b45309',
      borderColor: '#fde68a',
    };
  }

  if (row.status === 'pending_employee') {
    return {
      shortLabel: 'Pendiente empleado',
      fullLabel,
      color: 'default',
      variant: 'outlined',
      bgColor: '#f8fafc',
      textColor: '#475569',
      borderColor: '#e2e8f0',
    };
  }

  if (row.status === 'pending_responsible') {
    return {
      shortLabel: 'Pendiente de responsable',
      fullLabel,
      color: 'default',
      variant: 'outlined',
      bgColor: '#faf5ff',
      textColor: '#7e22ce',
      borderColor: '#e9d5ff',
    };
  }

  if (row.status === 'pending_others') {
    const isPersonas = fullLabel.toLowerCase().includes('personas') || fullLabel.toLowerCase().includes('dept.');
    if (fullLabel.toLowerCase().includes('responsable')) {
      return {
        shortLabel: 'Pendiente de responsable',
        fullLabel,
        color: 'default',
        variant: 'outlined',
        bgColor: '#faf5ff',
        textColor: '#7e22ce',
        borderColor: '#e9d5ff',
      };
    }
    return {
      shortLabel: isPersonas ? 'Pendiente Dept. Personas' : 'Pendiente empleado',
      fullLabel,
      color: 'default',
      variant: 'outlined',
      bgColor: isPersonas ? '#f0f9ff' : '#f8fafc',
      textColor: isPersonas ? '#0369a1' : '#475569',
      borderColor: isPersonas ? '#bae6fd' : '#e2e8f0',
    };
  }

  return {
    shortLabel: fullLabel,
    fullLabel,
    color: 'default',
    variant: 'outlined',
    bgColor: '#f8fafc',
    textColor: '#475569',
    borderColor: '#e2e8f0',
  };
}

function getDisplayedFinalQuantity(row) {
  return Number(row?.finalQuantity ?? row?.theoreticalQuantity ?? 0);
}

function getDisplayedComment(row) {
  if (row?.comment && row.comment.trim()) {
    return row.comment;
  }
  const validationWithComment = [...(row?.validations || [])]
    .reverse()
    .find((v) => v.comment && v.comment.trim() && !v.comment.startsWith('Validado por:'));
  return validationWithComment?.comment || '';
}

function renderShiftBadge(shiftType) {
  if (!shiftType) {
    return <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>-</Typography>;
  }

  const shift = String(shiftType).toUpperCase();
  if (shift === 'MANANA') {
    return (
      <Tooltip title="Turno Mañana (Salesforce)" arrow>
        <Chip
          icon={<SunIcon sx={{ fontSize: '13px !important', color: '#d97706 !important' }} />}
          label="Mañana"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: '#fffbeb',
            color: '#b45309',
            border: '1px solid #fde68a',
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      </Tooltip>
    );
  }

  if (shift === 'TARDE') {
    return (
      <Tooltip title="Turno Tarde (Salesforce)" arrow>
        <Chip
          icon={<TwilightIcon sx={{ fontSize: '13px !important', color: '#2563eb !important' }} />}
          label="Tarde"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: '#eff6ff',
            color: '#1d4ed8',
            border: '1px solid #bfdbfe',
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      </Tooltip>
    );
  }

  if (shift === 'NOCHE') {
    return (
      <Tooltip title="Turno Noche (Salesforce)" arrow>
        <Chip
          icon={<MoonIcon sx={{ fontSize: '13px !important', color: '#7c3aed !important' }} />}
          label="Noche"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: '#f5f3ff',
            color: '#6d28d9',
            border: '1px solid #ddd6fe',
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      </Tooltip>
    );
  }

  if (shift === 'GUARDIA') {
    return (
      <Tooltip title="Turno Guardia (Salesforce)" arrow>
        <Chip
          icon={<ShieldIcon sx={{ fontSize: '13px !important', color: '#059669 !important' }} />}
          label="Guardia"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: '#ecfdf5',
            color: '#047857',
            border: '1px solid #a7f3d0',
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      label={shift}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        bgcolor: '#f1f5f9',
        color: '#475569',
        border: '1px solid #cbd5e1',
      }}
    />
  );
}

function buildQueryParams(filters, extra = {}) {
  const params = {
    ...extra,
  };

  if (filters.extraDateFrom) {
    params.dateFrom = filters.extraDateFrom;
  }

  if (filters.extraDateTo) {
    params.dateTo = filters.extraDateTo;
  }

  if (filters.paymentDateFrom) {
    params.paymentDateFrom = filters.paymentDateFrom;
  }

  if (filters.paymentDateTo) {
    params.paymentDateTo = filters.paymentDateTo;
  }

  Object.entries(filters).forEach(([key, value]) => {
    if ((
      key === 'extraDateFrom'
      || key === 'extraDateTo'
      || key === 'paymentDateFrom'
      || key === 'paymentDateTo'
    )) {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params[key] = value.join(',');
      }
    } else if (value && value !== 'all') {
      params[key] = value;
    }
  });

  return params;
}


export default function ExtrasView({ fixedTab = null }) {
  const [extraTypeInfoOpen, setExtraTypeInfoOpen] = useState(false);
  const [extraTypeConfigs, setExtraTypeConfigs] = useState([]);
  const [extraTypeConfigFilter, setExtraTypeConfigFilter] = useState('');
  const [loadingExtraTypes, setLoadingExtraTypes] = useState(false);
  const handleOpenExtraTypeInfo = async () => {
    setExtraTypeInfoOpen(true);
    setExtraTypeConfigFilter('');
    setLoadingExtraTypes(true);
    try {
      const configs = await fetchExtraTypeConfigList();

      const rows = configs.map(cfg => ({
        description: cfg.extraTypeName || '-',
        delegation: cfg.delegationName || '-',
        price: cfg.amount ?? '-',
      }));
      setExtraTypeConfigs(rows);
    } catch (e) {
      toast.error('No se pudo cargar la lista de tipos de extra');
    } finally {
      setLoadingExtraTypes(false);
    }
  };
  const { t } = useTranslation();
  const { checkPermission, getMeFromLocalStorage, isAdmin } = useAuthContext();
  const currentUser = getMeFromLocalStorage?.();
  const hasMaintenanceFull = checkPermission(EXTRAS_MANTENIMIENTO) || isAdmin();
  const hasMaintenanceEmployee = checkPermission(EXTRAS_EMPLOYE);
  const hasMaintenanceValidator = checkPermission(EXTRAS_VALIDATOR);
  const hasRepairEmployee = checkPermission(EXTRAS_REPARACIONES_EMPLEADO);
  const hasRepairValidator = checkPermission(EXTRAS_REPARACIONES_VALIDADOR) || isAdmin();

  const canAccessMaintenance = hasMaintenanceFull || hasMaintenanceEmployee || hasMaintenanceValidator;
  const canAccessRepairs = hasRepairEmployee || hasRepairValidator;
  const canAccessExtras = canAccessMaintenance || canAccessRepairs;

  const defaultTab = fixedTab || (canAccessMaintenance ? 'postventa' : (canAccessRepairs ? 'reparaciones' : 'postventa'));
  const [activeTab, setActiveTab] = useState(defaultTab);

  const hasFullAccessForActiveTab = activeTab === 'reparaciones' ? hasRepairValidator : hasMaintenanceFull;

  const filteredStatusOptions = STATUS_OPTIONS.filter((option) => {
    if (option.value === 'pending_mine') {
      return !hasFullAccessForActiveTab;
    }
    if (option.value === 'pending_employee') {
      return hasFullAccessForActiveTab || (activeTab === 'reparaciones' ? hasRepairValidator : hasMaintenanceValidator);
    }
    if (option.value === 'pending_responsible') {
      return hasFullAccessForActiveTab || !(activeTab === 'reparaciones' ? hasRepairValidator : hasMaintenanceValidator);
    }
    return true;
  });

  const getValidationStatusLabel = (status) => {
    const statusMap = {
      pending: t('extra.afterSalesExtra.validation.status.pending', 'Pendiente'),
      approved: t('extra.afterSalesExtra.validation.status.approved', 'Aprobada'),
      rejected: t('extra.afterSalesExtra.validation.status.rejected', 'Rechazada'),
      cancelled: t('extra.afterSalesExtra.validation.status.cancelled', 'Cancelada'),
    };
    return statusMap[status] || status;
  };
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [availableExtraTypes, setAvailableExtraTypes] = useState([]);
  const limit = 100;
  const loadMoreRef = useRef(null);
  const rowsLengthRef = useRef(0);
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [allEmployeeOptions, setAllEmployeeOptions] = useState([]);
  const [allValidatorOptions, setAllValidatorOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [extraDateRangeAnchorEl, setExtraDateRangeAnchorEl] = useState(null);
  const [paymentDateRangeAnchorEl, setPaymentDateRangeAnchorEl] = useState(null);
  const [validationsDialogRow, setValidationsDialogRow] = useState(null);
  const [detailsDialogRow, setDetailsDialogRow] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [expandedTypeRows, setExpandedTypeRows] = useState({});
  const [expandedCommentRows, setExpandedCommentRows] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [showValidations, setShowValidations] = useState({});
  const [showDetails, setShowDetails] = useState({});

  const [deadlineNoticeOpen, setDeadlineNoticeOpen] = useState(() => new Date().getDate() > 20);
  const [excelNoticeOpen, setExcelNoticeOpen] = useState(false);

  const handleCloseDeadlineNotice = () => {
    setDeadlineNoticeOpen(false);
  };
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [exportingMatrix, setExportingMatrix] = useState(false);
  const [validatingIds, setValidatingIds] = useState([]);
  const [hasLoadedMore, setHasLoadedMore] = useState(false);
  const canFilterEmployee = activeTab === 'reparaciones'
    ? hasRepairValidator
    : (hasMaintenanceFull || hasMaintenanceValidator);
  const canFilterValidator = activeTab === 'reparaciones'
    ? hasRepairValidator
    : hasMaintenanceFull;
  const canManageManualExtras = (activeTab === 'reparaciones' ? hasRepairValidator : hasMaintenanceValidator) || isAdmin();
  const isOperarioOnly = activeTab === 'reparaciones'
    ? (hasRepairEmployee && !hasRepairValidator)
    : (hasMaintenanceEmployee && !hasMaintenanceValidator && !hasMaintenanceFull);

  useEffect(() => {
    if (fixedTab) {
      setActiveTab(fixedTab);
    } else if (!canAccessMaintenance && canAccessRepairs && activeTab !== 'reparaciones') {
      setActiveTab('reparaciones');
    }
  }, [fixedTab, canAccessMaintenance, canAccessRepairs, activeTab]);
  const [delegations, setDelegations] = useState([]);
  const [annotationRow, setAnnotationRow] = useState(null);
  const [annotationText, setAnnotationText] = useState('');
  const [savingAnnotation, setSavingAnnotation] = useState(false);
  const [isCurrentUserInHumanResources, setIsCurrentUserInHumanResources] = useState(null);
  const [humanResourcesValidationModalOpen, setHumanResourcesValidationModalOpen] = useState(false);
  const [checkingHumanResourcesAccess, setCheckingHumanResourcesAccess] = useState(false);
  const [extraToDelete, setExtraToDelete] = useState(null);
  const [deletingExtra, setDeletingExtra] = useState(false);
  const [createForm, setCreateForm] = useState({
    delegation: null,
    user: null,
    extraConfig: null,
    amount: '',
    extraDate: '',
    comment: '',
    numParte: '',
    inicioEngrase: '',
    finEngrase: '',
    trabajoFueraDeJornada: true,
    warningNumber: '',
    warningStartDate: '',
    warningEndDate: '',
    latEnd: '',
    lonEnd: '',
    warningReason: '',
    vip: false,
    accidente: false,
    rescate: false,
    urgente: false,
    incidencia: false,
    parado: false,
  });
  const [allExtraConfigs, setAllExtraConfigs] = useState([]);
  const [loadingExtraConfigs, setLoadingExtraConfigs] = useState(false);

  const isAfterSales = canAccessExtras;

  const uniqueDelegations = useMemo(() => {
    const map = new Map();
    allExtraConfigs.forEach((cfg) => {
      if (cfg.delegationId && cfg.delegationName) {
        map.set(cfg.delegationId, {
          id: cfg.delegationId,
          name: cfg.delegationName,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
  }, [allExtraConfigs]);

  const sortedDelegations = useMemo(() => {
    return [...delegations].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
    );
  }, [delegations]);

  const filteredExtraConfigsForCreation = useMemo(() => {
    if (!createForm.delegation) {
      return [];
    }
    const targetDate = createForm.extraDate ? new Date(`${createForm.extraDate}T12:00:00`) : new Date();
    return allExtraConfigs
      .filter((cfg) => {
        if (cfg.delegationId !== createForm.delegation.id) {
          return false;
        }
        const startDate = new Date(cfg.startDate);
        const endDate = new Date(cfg.endDate);
        return startDate <= targetDate && endDate >= targetDate;
      })
      .sort((a, b) =>
        (a.extraTypeName || '').localeCompare(b.extraTypeName || '', 'es', { sensitivity: 'base' })
      );
  }, [allExtraConfigs, createForm.delegation, createForm.extraDate]);

  const isExtraManualSelected = useMemo(() => {
    const typeName = (createForm.extraConfig?.extraTypeName || '').toLowerCase().trim();
    return typeName === 'extra manual' || createForm.extraConfig?.extraTypeId === 61;
  }, [createForm.extraConfig]);

useEffect(() => {
    rowsLengthRef.current = rows.length;
}, [rows.length]);

useEffect(() => {
    loadingRef.current = loading;
}, [loading]);

useEffect(() => {
    loadingMoreRef.current = loadingMore;
}, [loadingMore]);

useEffect(() => {
    hasMoreRef.current = hasMore;
}, [hasMore]);

useEffect(() => {
    if (currentUser) {
      setIsCurrentUserInHumanResources(Boolean(currentUser.isHumanResources));
    }
}, [currentUser]);

useEffect(() => {
    if (createDialogOpen) {
      setLoadingExtraConfigs(true);
      fetchExtraTypeConfigList()
        .then((configs) => {
          setAllExtraConfigs(configs || []);
        })
        .catch((err) => {
          console.error('Error al cargar tipos de extra:', err);
          toast.error('No se pudieron cargar los tipos de extra.');
        })
        .finally(() => {
          setLoadingExtraConfigs(false);
        });
    }
}, [createDialogOpen]);

  useEffect(() => {
    getDelegationList({ limit: 9999 })
      .then((res) => {
        const list = res?.records || res?.items || (Array.isArray(res) ? res : []);
        const sorted = [...list].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        );
        setDelegations(sorted);
      })
      .catch((err) => {
        console.error('Error al cargar delegaciones:', err);
      });
  }, []);

  useEffect(() => {
    async function loadSummary() {
      if (!isAfterSales) {
        setSummary(DEFAULT_SUMMARY);
        return;
      }

      if (activeTab === 'reparaciones') {
        return;
      }

      try {
        const response = await getExtrasSummary(buildQueryParams(filters));
        const total = response?.total ?? 0;
        setSummary({
          total,
          totalTheoretical: response?.totalTheoretical ?? 0,
          totalFinal: response?.totalFinal ?? 0,
          byType: response?.byType ?? [],
        });
        setTotalCount((prev) => (prev === 0 ? total : prev));
      } catch (error) {
        console.error('Error al cargar el resumen de extras:', error);
      }
    }

    loadSummary();
  }, [filters, isAfterSales, activeTab]);

useEffect(() => {
    async function loadFilterOptions() {
      if (!isAfterSales) {
        return;
      }

      try {
        const options = await getExtrasFilterOptions();
        if (Array.isArray(options?.employees) && options.employees.length > 0) {
          setAllEmployeeOptions((prev) => {
            const merged = Array.from(new Set([...prev, ...options.employees]));
            return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
          });
        }
        if (Array.isArray(options?.validators) && options.validators.length > 0) {
          setAllValidatorOptions((prev) => {
            const merged = Array.from(new Set([...prev, ...options.validators]));
            return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
          });
        }
        if (Array.isArray(options?.extraTypes) && options.extraTypes.length > 0) {
          setAvailableExtraTypes((prev) => {
            const merged = Array.from(new Set([...prev, ...options.extraTypes]));
            return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
          });
        }
      } catch (err) {
        console.error('Error al cargar opciones de filtros para extras:', err);
      }
    }

    loadFilterOptions();
}, [isAfterSales]);

useEffect(() => {
    async function loadUsers() {
    if (!canManageManualExtras || !createDialogOpen || users.length > 0) {
        return;
      }

      setLoadingUsers(true);

      try {
        const response = await getUsersList({ limit: 1000, offset: 0, filterEnabled: true, filterTerminationDate: false });
        setUsers(response?.records ?? []);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
      } finally {
        setLoadingUsers(false);
      }
    }

    loadUsers();
}, [canManageManualExtras, createDialogOpen, users.length]);

const loadExtras = async ({ reset = false } = {}) => {
    if (!isAfterSales) {
      setRows([]);
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (!reset && (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current)) {
      return;
    }

    const currentOffset = reset ? 0 : rowsLengthRef.current;

    if (reset) {
      loadingRef.current = true;
      setLoading(true);
      setHasMore(true);
      setHasLoadedMore(false);
    } else {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      setHasLoadedMore(true);
    }

    try {
      const queryParams = buildQueryParams(filters, {
        limit,
        offset: currentOffset,
        sortBy: sortField,
        sortOrder: sortDirection,
      });

      let response;
      if (activeTab === 'reparaciones') {
        response = await getRepairExtraList(queryParams);
        if (response?.totalTheoretical !== undefined) {
          setSummary({
            total: response.total ?? 0,
            totalTheoretical: Number(response.totalTheoretical ?? 0),
            totalFinal: Number(response.totalFinal ?? 0),
            trimmedHoursTheoretical: Number(response.trimmedHoursTheoretical ?? 0),
            trimmedHoursFinal: Number(response.trimmedHoursFinal ?? 0),
            fixedAmountTheoretical: Number(response.fixedAmountTheoretical ?? 0),
            fixedAmountFinal: Number(response.fixedAmountFinal ?? 0),
            byType: [],
          });
        }
      } else {
        response = await getAfterSalesExtraList(queryParams);
      }

      const records = response?.records || response?.results || response?.items || (Array.isArray(response) ? response : []);
      const total = response?.total ?? 0;

      setTotalCount(total);
      setRows((previousRows) => (reset ? records : [...previousRows, ...records]));
      const hasNextPage = records.length > 0 && (currentOffset + records.length < total);
      setHasMore(hasNextPage);
      hasMoreRef.current = hasNextPage;

      if (activeTab === 'postventa') {
        // Acumular tipos de extra disponibles
        setAvailableExtraTypes((prev) => {
          const newTypes = records
            .map((r) => (r.extraTypeDescription || r.extraType || '').trim())
            .filter((t) => Boolean(t) && t !== '-');
          const merged = Array.from(new Set([...prev, ...newTypes]));
          return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
        });
      }

      // Acumular opciones de empleado y validador (nunca se vacían)
      setAllEmployeeOptions((prev) => {
        const newNames = records.map((r) => (r.employeeName || '').trim()).filter(Boolean);
        const merged = Array.from(new Set([...prev, ...newNames]));
        return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      });
      setAllValidatorOptions((prev) => {
        const newNames = records
        .flatMap((r) => {
          const validations = r.validations || [];
          const maxPos = Math.max(...validations.map((v) => v.validationPosition ?? 0), 0);
          return validations
            .filter((v) => {
              const pos = v.validationPosition ?? 0;
              return activeTab === 'reparaciones'
                ? pos < maxPos
                : pos > 1 && (maxPos <= 2 || pos < maxPos);
            })
            .map((v) => (v.validatorName || '').trim());
        })
          .filter(Boolean);
        const merged = Array.from(new Set([...prev, ...newNames]));
        return merged.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      });
    } catch (error) {
      console.error('Error al cargar extras:', error);
    } finally {
      loadingRef.current = false;
      loadingMoreRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
};

  useEffect(() => {
    setSelectedIds([]);
    loadExtras({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isAfterSales, sortField, sortDirection, activeTab]);

useEffect(() => {
    if (!hasMore || loading) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadExtras();
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 400px 0px',
        threshold: 0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    const handleScroll = () => {
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) {
        return;
      }
      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollY + windowHeight >= documentHeight - 500) {
        loadExtras();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [hasMore, loading]);

const filteredRows = useMemo(() => {
    if (filters.incidence === 'all') {
      return rows;
    }
    const wantsIncidence = filters.incidence === 'yes';
    return rows.filter((row) => Boolean(row.hasIncidence) === wantsIncidence);
}, [rows, filters.incidence]);

const employeeFilterOptions = allEmployeeOptions;

const validatorFilterOptions = allValidatorOptions;

  const stats = useMemo(() => {
    if (activeTab === 'reparaciones') {
      return {
        totalRecords: totalCount,
        sumTheoretical: summary.totalTheoretical ?? 0,
        sumFinal: summary.totalFinal ?? 0,
      };
    }
    return {
      totalRecords: summary.total,
      sumTheoretical: summary.totalTheoretical,
      sumFinal: summary.totalFinal,
    };
  }, [activeTab, totalCount, summary]);

const handleRequestSort = (property) => {
    const isAsc = sortField === property && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortField(property);
    setSortDirection(newDirection);
};

const sortedRows = useMemo(() => {
    if (!sortField) {
      return filteredRows;
    }

    return [...filteredRows].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'type': {
          const typeA = (a.extraTypeDescription || a.extraType || '').trim();
          const typeB = (b.extraTypeDescription || b.extraType || '').trim();
          comparison = typeA.localeCompare(typeB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'employee': {
          const empA = (a.employeeName || '').trim();
          const empB = (b.employeeName || '').trim();
          comparison = empA.localeCompare(empB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'extraDate': {
          const dateA = a.extraDate ? new Date(a.extraDate).getTime() : 0;
          const dateB = b.extraDate ? new Date(b.extraDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        case 'shiftType': {
          const shiftA = (a.shiftType || '').trim();
          const shiftB = (b.shiftType || '').trim();
          comparison = shiftA.localeCompare(shiftB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'paymentDate': {
          const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
          const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        case 'theoreticalQuantity': {
          const qtyA = Number(a.theoreticalQuantity) || 0;
          const qtyB = Number(b.theoreticalQuantity) || 0;
          comparison = qtyA - qtyB;
          break;
        }
        case 'finalQuantity': {
          const qtyA = Number(getDisplayedFinalQuantity(a)) || 0;
          const qtyB = Number(getDisplayedFinalQuantity(b)) || 0;
          comparison = qtyA - qtyB;
          break;
        }
        case 'status': {
          const statusA = (getStatusPresentation(a).shortLabel || '').trim();
          const statusB = (getStatusPresentation(b).shortLabel || '').trim();
          comparison = statusA.localeCompare(statusB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'incidence': {
          const incA = a.hasIncidence ? 1 : 0;
          const incB = b.hasIncidence ? 1 : 0;
          comparison = incA - incB;
          break;
        }
        case 'workOrder': {
          const otA = (a.workOrder || '').trim();
          const otB = (b.workOrder || '').trim();
          comparison = otA.localeCompare(otB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'delegationName': {
          const delA = (a.delegationName || '').trim();
          const delB = (b.delegationName || '').trim();
          comparison = delA.localeCompare(delB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'agreedAmount': {
          comparison = (Number(a.agreedAmount) || 0) - (Number(b.agreedAmount) || 0);
          break;
        }
        case 'agreedHours': {
          comparison = (Number(a.agreedHours) || 0) - (Number(b.agreedHours) || 0);
          break;
        }
        case 'performedHours': {
          comparison = (Number(a.performedHours) || 0) - (Number(b.performedHours) || 0);
          break;
        }
        case 'trimmedHours': {
          comparison = (Number(a.trimmedHours) || 0) - (Number(b.trimmedHours) || 0);
          break;
        }
        case 'comment': {
          const comA = (getDisplayedComment(a) || '').trim();
          const comB = (getDisplayedComment(b) || '').trim();
          comparison = comA.localeCompare(comB, 'es', { sensitivity: 'base' });
          break;
        }
        case 'id':
        default: {
          comparison = (a.id || 0) - (b.id || 0);
          break;
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
}, [filteredRows, sortField, sortDirection]);

const selectedRows = useMemo(
    () => sortedRows.filter((row) => selectedIds.includes(row.id)),
    [sortedRows, selectedIds],
);

const filteredExtraTypeConfigs = useMemo(() => {
    const query = extraTypeConfigFilter.trim().toLowerCase();

    const visibleRows = extraTypeConfigs.filter((row) => {
      if (!query) {
        return true;
      }

      return [row.description, row.delegation, String(row.price)]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });

    return visibleRows.sort((a, b) => {
      const delegationCompare = (a.delegation || '').localeCompare(b.delegation || '', 'es', { sensitivity: 'base' });

      if (delegationCompare !== 0) {
        return delegationCompare;
      }

      return (a.description || '').localeCompare(b.description || '', 'es', { sensitivity: 'base' });
    });
}, [extraTypeConfigs, extraTypeConfigFilter]);

const extraDateRangePickerOpen = Boolean(extraDateRangeAnchorEl);
const paymentDateRangePickerOpen = Boolean(paymentDateRangeAnchorEl);

const toggleTypeExpanded = (rowId) => {
    setExpandedTypeRows((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
};

const toggleCommentExpanded = (rowId) => {
    setExpandedCommentRows((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
};

const toggleCardExpanded = (rowId) => {
    setExpandedCards((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
};

const toggleShowValidations = (rowId) => {
    setShowValidations((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
};

const toggleShowDetails = (rowId) => {
    setShowDetails((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
};

const allVisibleSelected = sortedRows.length > 0 && sortedRows.every((row) => selectedIds.includes(row.id));

const resetCreateForm = () => {
    setCreateForm({
      delegation: null,
      user: null,
      extraConfig: null,
      amount: '',
      extraDate: '',
      comment: '',
      numParte: '',
      inicioEngrase: '',
      finEngrase: '',
      trabajoFueraDeJornada: true,
      warningNumber: '',
      warningStartDate: '',
      warningEndDate: '',
      latEnd: '',
      lonEnd: '',
      warningReason: '',
      vip: false,
      accidente: false,
      rescate: false,
      urgente: false,
      incidencia: false,
      parado: false,
    });
};

const handleDateRangeChange = (field, value) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [field]: value ? value.format('YYYY-MM-DD') : '',
    }));
};

  const refreshCurrentData = async () => {
    if (activeTab === 'reparaciones') {
      await loadExtras({ reset: true });
    } else {
      await Promise.all([
        loadExtras({ reset: true }),
        (async () => {
          const response = await getExtrasSummary(buildQueryParams(filters));
          setSummary({
            total: response?.total ?? 0,
            totalTheoretical: response?.totalTheoretical ?? 0,
            totalFinal: response?.totalFinal ?? 0,
            byType: response?.byType ?? [],
          });
        })(),
      ]);
    }
  };

const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((previous) => previous.filter((id) => !sortedRows.some((row) => row.id === id)));
      return;
    }

    setSelectedIds((previous) => Array.from(new Set([...previous, ...sortedRows.map((row) => row.id)])));
};

const toggleRowSelection = (rowId) => {
    setSelectedIds((previous) => previous.includes(rowId)
      ? previous.filter((id) => id !== rowId)
      : [...previous, rowId]);
};

const findActionablePendingValidation = (row) => {
    const pendingValidations = (row.validations || [])
      .filter((validation) => validation.status === 'pending')
      .sort((left, right) => (left.validationPosition ?? Number.MAX_SAFE_INTEGER) - (right.validationPosition ?? Number.MAX_SAFE_INTEGER));

    return pendingValidations[0] || null;
};

const findPendingValidationForCurrentUser = (row) => {
    const actionableValidation = findActionablePendingValidation(row);

    if (!actionableValidation) {
      return null;
    }

    if (actionableValidation.validatorId === currentUser?.id) {
      return actionableValidation;
    }

    if (
      actionableValidation.validatorId === null &&
    (actionableValidation.validationPosition ?? 0) >= 2 &&
      isCurrentUserInHumanResources === true
    ) {
      return actionableValidation;
    }

    return null;
};

const findValidationForCurrentUser = (row) => {
    const assignedValidation = (row.validations || []).find(
      (validation) => validation.validatorId === currentUser?.id,
    );
    if (assignedValidation) {
      return assignedValidation;
    }

    if (isCurrentUserInHumanResources === true) {
      const rrhhValidation = (row.validations || []).find(
      (validation) => validation.validatorId === null && (validation.validationPosition ?? 0) >= 2
      );
      if (rrhhValidation) {
        return rrhhValidation;
      }
    }

    return null;
};







const getCurrentUserLabel = () => {
    const parts = [
      currentUser?.name,
      currentUser?.first_surname ?? currentUser?.firstSurname,
      currentUser?.second_surname ?? currentUser?.secondSurname,
    ].filter(Boolean);

    const fullName = parts.join(' ').trim() || currentUser?.fullName || currentUser?.email || 'Usuario';

    return fullName.toUpperCase();
};

const hasPendingValidation = (row) => (row.validations || []).some((validation) => validation.status === 'pending');

const canEditAmount = (row) => {
    if (hasFullAccessForActiveTab) {
      return hasPendingValidation(row);
    }
    const pendingValidation = findPendingValidationForCurrentUser(row);
    if (!pendingValidation) {
      return false;
    }
    return (pendingValidation.validationPosition ?? 0) >= 1;
};

const ensureHumanResourcesValidationAccess = async () => {
    if (activeTab === 'reparaciones' || !hasMaintenanceFull) {
      return true;
    }

    if (isCurrentUserInHumanResources === true) {
      return true;
    }

    if (isCurrentUserInHumanResources === false) {
      setHumanResourcesValidationModalOpen(true);
      return false;
    }

    if (!currentUser?.id) {
      setHumanResourcesValidationModalOpen(true);
      return false;
    }

    setCheckingHumanResourcesAccess(true);

    try {
      const orgChartNodes = await fetchOrgChart(null, { includeDetails: true });
      const humanResourcesUserIds = new Set(
        (orgChartNodes || [])
          .filter((node) => (node.chartType || '').toUpperCase() === HUMAN_RESOURCES_STRUCTURE_NAME)
          .map((node) => node.userId)
          .filter(Boolean),
      );

      const isInHumanResources = humanResourcesUserIds.has(currentUser.id);
      setIsCurrentUserInHumanResources(isInHumanResources);

      if (!isInHumanResources) {
        setHumanResourcesValidationModalOpen(true);
      }

      return isInHumanResources;
    } catch (error) {
      console.error('Error al verificar pertenencia a RRHH:', error);
      toast.error('No se pudo verificar si perteneces a RRHH para validar.');
      return false;
    } finally {
      setCheckingHumanResourcesAccess(false);
    }
};

const validateRows = async (rowsToValidate) => {
    if (activeTab === 'postventa' && hasMaintenanceFull) {
      const canValidateAsAdmin = await ensureHumanResourcesValidationAccess();
      if (!canValidateAsAdmin) {
        return;
      }
    }

    const actionableRows = hasFullAccessForActiveTab
      ? rowsToValidate.filter((row) => hasPendingValidation(row))
      : rowsToValidate.filter((row) => findPendingValidationForCurrentUser(row));

    if (actionableRows.length === 0) {
      toast.info(hasFullAccessForActiveTab ? 'No hay extras pendientes en la selección actual.' : 'No hay extras pendientes de validar por ti en la selección actual.');
      return;
    }

    setValidatingIds(actionableRows.map((row) => row.id));

    try {
      const adminValidationComment = `Validado por: ${getCurrentUserLabel()}`;

      await Promise.all(actionableRows.map(async (row) => {
        if (hasFullAccessForActiveTab) {
          const pendingValidations = [...(row.validations || [])]
            .filter((validation) => validation.status === 'pending')
            .sort((left, right) => (left.validationPosition ?? Number.MAX_SAFE_INTEGER) - (right.validationPosition ?? Number.MAX_SAFE_INTEGER));

        await Promise.all(pendingValidations.map((validation) => {
          const isOwnValidation = validation.validatorId === currentUser?.id
            || (isCurrentUserInHumanResources === true && validation.validatorId === null);

          const patchPayload = {
            status: 'approved',
            validationAmount: getDisplayedFinalQuantity(row),
          };

          if (!isOwnValidation) {
            patchPayload.comment = adminValidationComment;
          }

          return patchValidationExtra(validation.id, patchPayload);
        }));

          return;
        }

        const pendingValidation = findPendingValidationForCurrentUser(row);
        const patchData = {
          status: 'approved',
        };

        if ((pendingValidation?.validationPosition ?? 1) >= 1 || hasFullAccessForActiveTab) {
          patchData.validationAmount = getDisplayedFinalQuantity(row);
        }

        return patchValidationExtra(pendingValidation.id, patchData);
      }));

      toast.success(actionableRows.length === 1 ? 'Extra validada correctamente.' : 'Extras validadas correctamente.');
      setSelectedIds([]);
      await refreshCurrentData();
    } catch (error) {
      console.error('Error al validar extras:', error);
      toast.error('No se pudieron validar las extras seleccionadas.');
    } finally {
      setValidatingIds([]);
    }
};

const canDeleteExtra = (row) => {
  if (!row) return false;
  if (String(row.loadingOrigin || '').toLowerCase() !== 'manual') {
    return false;
  }
  if (row.status === 'validated') {
    return false;
  }
  if (isAdmin()) {
    return true;
  }
  if (row.createdByUserId && currentUser?.id === row.createdByUserId) {
    return true;
  }
  if (!row.createdByUserId && canManageManualExtras) {
    return true;
  }
  return false;
};

const handleConfirmDeleteExtra = async () => {
  if (!extraToDelete) return;
  setDeletingExtra(true);
  try {
    await deleteAfterSalesExtra(extraToDelete.id);
    toast.success('Extra manual eliminado correctamente.');
    setRows((previous) => previous.filter((r) => r.id !== extraToDelete.id));
    setExtraToDelete(null);
    await refreshCurrentData();
  } catch (error) {
    console.error('Error al eliminar extra manual:', error);
    toast.error(error.response?.data?.message || error.message || 'No se pudo eliminar el extra manual.');
  } finally {
    setDeletingExtra(false);
  }
};

const handleCreateExtra = async () => {
    if (!canManageManualExtras) {
      toast.error('Solo los validadores de posición 2 pueden añadir extras manuales.');
      return;
    }

  if (!createForm.delegation || !createForm.user || !createForm.extraConfig || !createForm.extraDate) {
    toast.error('Debes indicar delegación, empleado, tipo de extra y fecha del extra.');
      return;
    }

    if (isExtraManualSelected) {
      if (!createForm.comment?.trim()) {
        toast.error('Debes indicar en el comentario la descripción del porqué de este Extra Manual.');
        return;
      }
      const parsedAmount = Number(createForm.amount);
      if (createForm.amount === '' || isNaN(parsedAmount) || parsedAmount <= 0) {
        toast.error('Debes indicar un importe válido (mayor que 0) para el Extra Manual.');
        return;
      }
    }

    const className = createForm.extraConfig.className || '';
    const isPreventive = className.includes('AfterSalesPreventiveExtra');
    const isWarning = className.includes('AfterSalesWarningExtra');

    if (isPreventive) {
      if (!createForm.numParte || !createForm.inicioEngrase || !createForm.finEngrase) {
        toast.error('Debes indicar el número de parte y las fechas de inicio y fin de engrase.');
        return;
      }
    }

    if (isWarning) {
      if (
        !createForm.warningNumber ||
        !createForm.warningStartDate ||
        !createForm.warningEndDate ||
        !createForm.warningReason ||
        createForm.latEnd === '' ||
        createForm.lonEnd === ''
      ) {
        toast.error('Debes rellenar todos los campos obligatorios del aviso (número, fechas, motivo y coordenadas).');
        return;
      }
    }

    setSubmittingCreate(true);

    try {
      const extraDateStr = `${createForm.extraDate} 12:00:00`;
      const computedAmount = isExtraManualSelected
        ? Number(createForm.amount)
        : (createForm.amount !== '' && !isNaN(Number(createForm.amount))
            ? Number(createForm.amount)
            : Number(createForm.extraConfig.amount ?? 0));

      const payload = {
        userId: createForm.user.id,
        theoreticalQuantity: computedAmount,
        finalQuantity: computedAmount,
        loadingOrigin: 'Manual',
        paymentDate: extraDateStr,
        extraDate: extraDateStr,
        comment: createForm.comment?.trim() || null,
        extraTypeId: createForm.extraConfig.extraTypeId || null,
        delegationId: createForm.delegation.id || null,
      };

      if (isPreventive) {
        payload.numParte = createForm.numParte;
        payload.inicioEngrase = new Date(createForm.inicioEngrase).toISOString();
        payload.finEngrase = new Date(createForm.finEngrase).toISOString();
        payload.trabajoFueraDeJornada = Boolean(createForm.trabajoFueraDeJornada);
      }

      if (isWarning) {
        payload.warningNumber = createForm.warningNumber;
        payload.warningStartDate = new Date(createForm.warningStartDate).toISOString();
        payload.warningEndDate = new Date(createForm.warningEndDate).toISOString();
        payload.latEnd = Number(createForm.latEnd);
        payload.lonEnd = Number(createForm.lonEnd);
        payload.warningReason = createForm.warningReason;
        payload.vip = Boolean(createForm.vip);
        payload.accidente = Boolean(createForm.accidente);
        payload.rescate = Boolean(createForm.rescate);
        payload.urgente = Boolean(createForm.urgente);
        payload.incidencia = Boolean(createForm.incidencia);
        payload.parado = Boolean(createForm.parado);
      }

      await postAfterSalesExtra(payload);

      toast.success('Extra manual creada correctamente.');
      setCreateDialogOpen(false);
      resetCreateForm();
      await refreshCurrentData();
    } catch (error) {
      console.error('Error al crear extra manual:', error);
      toast.error('No se pudo crear la extra manual.');
    } finally {
      setSubmittingCreate(false);
    }
  };

  const startEditingRow = (row) => {
    const currentValidation = findValidationForCurrentUser(row);
    const existingComment = getDisplayedComment(row) || currentValidation?.comment || '';

    setEditingRow({
      id: row.id,
      theoreticalQuantity: String(row.theoreticalQuantity ?? ''),
      finalQuantity: String(getDisplayedFinalQuantity(row)),
      validationId: currentValidation?.id ?? null,
      comment: existingComment,
    });
  };

  const saveEditedRow = async () => {
    if (!editingRow) {
      return;
    }

    const amount = Number(editingRow.finalQuantity);

    if (Number.isNaN(amount)) {
      toast.error('El importe indicado no es válido.');
      return;
    }

    if (!editingRow.comment || !editingRow.comment.trim()) {
      toast.error('Debes indicar un comentario al cambiar el precio.');
      return;
    }

    setSavingEdit(true);

    try {
      if (activeTab === 'reparaciones') {
        await patchRepairExtra(editingRow.id, {
          finalQuantity: amount,
          comment: editingRow.comment.trim(),
        });
      } else if (editingRow.validationId) {
        await patchValidationExtra(editingRow.validationId, {
          validationAmount: amount,
          comment: editingRow.comment.trim(),
        });
      } else {
        await patchAfterSalesExtra(editingRow.id, {
          finalQuantity: amount,
          comment: editingRow.comment.trim(),
        });
      }

      toast.success('Importe actualizado correctamente.');
      setEditingRow(null);
      await refreshCurrentData();
    } catch (error) {
      console.error('Error al actualizar el importe:', error);
      toast.error('No se pudo actualizar el importe.');
    } finally {
      setSavingEdit(false);
    }
  };

  const saveAnnotation = async () => {
    if (!annotationRow) {
      return;
    }

    setSavingAnnotation(true);
    try {
      if (activeTab === 'reparaciones') {
        await patchRepairExtra(annotationRow.id, {
          comment: annotationText.trim(),
        });
      } else {
        await patchAfterSalesExtra(annotationRow.id, {
          comment: annotationText.trim(),
        });
      }

      toast.success('Anotación guardada correctamente.');
      setAnnotationRow(null);
      await refreshCurrentData();
    } catch (error) {
      console.error('Error al guardar anotación:', error);
      toast.error('No se pudo guardar la anotación.');
    } finally {
      setSavingAnnotation(false);
    }
  };

  const handleExport = async () => {
    setExcelNoticeOpen(true);
    return;

    // eslint-disable-next-line no-unreachable
    try {
      setExportingMatrix(true);
      toast.info('Generando Excel con la matriz de extras (BBDD)...');

      let targetYear;
      let targetMonth;
      if (filters.extraDateFrom) {
        const d = new Date(filters.extraDateFrom);
        if (!isNaN(d.getTime())) {
          targetYear = d.getFullYear();
          targetMonth = d.getMonth() + 1;
        }
      } else if (filters.paymentDateFrom) {
        const d = new Date(filters.paymentDateFrom);
        if (!isNaN(d.getTime())) {
          targetYear = d.getFullYear();
          targetMonth = d.getMonth() + 1;
        }
      }

      await exportExtrasMatrix({
        year: targetYear,
        month: targetMonth,
        dateFrom: filters.extraDateFrom,
        dateTo: filters.extraDateTo,
      });
    } catch (error) {
      console.error('Error al exportar matriz de extras:', error);
      toast.error('No se pudo generar la matriz de extras.');
    } finally {
      setExportingMatrix(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10, gap: 2 }}>
        <CircularProgress size={60} thickness={4} />
        <Typography color="textSecondary">{t('ui.loading', 'Cargando extras...')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2.5, mb: 4, pb: 12 }}>
      {/* Cabecera y Aviso de Plazos */}
      <Box
        sx={{
          mb: 2.5,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: '#e0f2fe',
              color: '#0284c7',
              border: '1px solid #bae6fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px -2px rgba(2, 132, 199, 0.18)',
              flexShrink: 0,
            }}
          >
            {activeTab === 'reparaciones' ? (
              <HandymanIcon sx={{ fontSize: 24, color: '#0284c7' }} />
            ) : (
              <ReceiptLongIcon sx={{ fontSize: 24, color: '#0284c7' }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                lineHeight: 1.2,
                fontSize: { xs: '1.25rem', sm: '1.45rem' },
                letterSpacing: '-0.01em',
              }}
            >
              {activeTab === 'reparaciones' ? 'Extras Reparaciones' : 'Extras Mantenimiento'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem', mt: 0.3 }}>
              {activeTab === 'reparaciones'
                ? 'Control y validación de horas en órdenes de trabajo de reparaciones'
                : 'Revisión y validación de extras de postventa'}
            </Typography>
          </Box>
        </Box>

        {activeTab === 'postventa' && (
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: { xs: '100%', md: 800 },
              p: 1.5,
              px: 2,
              borderRadius: 2.5,
              bgcolor: '#fffbeb',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #f59e0b',
              boxShadow: '0 1px 4px rgba(245, 158, 11, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                color: '#d97706',
                flexShrink: 0,
                fontSize: '0.95rem',
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              !
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.25,
                    bgcolor: '#f59e0b',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.675rem',
                    borderRadius: '50px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  Plazos de validación importantes
                </Box>
              </Stack>

              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', lineHeight: 1.35 }}>
                  • Extras no validados por el empleado antes del <b>día 5 a las 23:59h</b> no se validarán por Personas hasta el mes siguiente.
                </Typography>
                <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', lineHeight: 1.35 }}>
                  • Solo se pagarán los extras validados por el responsable antes del <b>día 20 a las 23:59h</b> (los posteriores se abonarán al mes siguiente).
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      {/* Selector de Pestañas: solo si no está fijado por ruta y tiene acceso a ambos módulos */}
      {!fixedTab && canAccessMaintenance && canAccessRepairs && (
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: 0.6,
            borderRadius: 3,
            bgcolor: '#f1f5f9',
            border: '1.5px solid #e2e8f0',
            display: 'inline-flex',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => {
              setActiveTab(val);
              setSelectedIds([]);
              setFilters(DEFAULT_FILTERS);
            }}
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab
              value="postventa"
              label="Extras Mantenimiento"
              sx={{
                borderRadius: 2.5,
                minHeight: 38,
                py: 0.8,
                px: 3,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: activeTab === 'postventa' ? '#ffffff !important' : '#64748b',
                bgcolor: activeTab === 'postventa' ? '#0284c7' : 'transparent',
                boxShadow: activeTab === 'postventa' ? '0 2px 8px rgba(2, 132, 199, 0.3)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: activeTab === 'postventa' ? '#ffffff' : '#0f172a',
                  bgcolor: activeTab === 'postventa' ? '#0284c7' : '#e2e8f0',
                },
              }}
            />
            <Tab
              value="reparaciones"
              label="Extras Reparaciones"
              sx={{
                borderRadius: 2.5,
                minHeight: 38,
                py: 0.8,
                px: 3,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: activeTab === 'reparaciones' ? '#ffffff !important' : '#64748b',
                bgcolor: activeTab === 'reparaciones' ? '#0284c7' : 'transparent',
                boxShadow: activeTab === 'reparaciones' ? '0 2px 8px rgba(2, 132, 199, 0.3)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: activeTab === 'reparaciones' ? '#ffffff' : '#0f172a',
                  bgcolor: activeTab === 'reparaciones' ? '#0284c7' : '#e2e8f0',
                },
              }}
            />
          </Tabs>
        </Paper>
      )}



      {/* Filtros Compactos justo antes de la tabla */}
      <Card
        sx={{
          mb: 2,
          boxShadow: '0 4px 16px -2px rgba(14, 165, 233, 0.05)',
          borderRadius: 3,
          border: '1.5px solid #e0f2fe',
          bgcolor: '#ffffff',
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={activeTab === 'reparaciones' ? 2.5 : 3}>
              <TextField
                fullWidth
                size="small"
                label={activeTab === 'reparaciones' ? "Buscar por OT, empleado o comentario" : "Buscar por origen o comentario"}
                value={filters.search}
                onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, search: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#0284c7' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {activeTab === 'postventa' ? (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel shrink>{t('ui.extras.filters.type', 'Tipo de Extra')}</InputLabel>
                  <Select
                    value={filters.extraType}
                    label={t('ui.extras.filters.type', 'Tipo de Extra')}
                    onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, extraType: e.target.value }))}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                  >
                    <MenuItem value="all">{t('ui.extras.filters.all', 'Todos los tipos')}</MenuItem>
                    {availableExtraTypes.map((typeName) => (
                      <MenuItem key={typeName} value={typeName}>
                        {typeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                {canFilterEmployee && (
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>Tipo de extra</InputLabel>
                      <Select
                        value={filters.repairExtraType}
                        label="Tipo de extra"
                        onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, repairExtraType: e.target.value }))}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                      >
                        {REPAIR_EXTRA_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={1.8}>
                  <FormControl fullWidth size="small">
                    <InputLabel shrink>Turno</InputLabel>
                    <Select
                      value={filters.shiftType}
                      label="Turno"
                      onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, shiftType: e.target.value }))}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                    >
                      {SHIFT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={canFilterEmployee ? 1.6 : 2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel shrink>Delegación OT</InputLabel>
                    <Select
                      value={filters.delegation}
                      label="Delegación OT"
                      onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, delegation: e.target.value }))}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                    >
                      <MenuItem value="all">Todas las delegaciones</MenuItem>
                      {sortedDelegations.map((del) => (
                        <MenuItem key={del.id} value={del.id}>
                          {del.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {canFilterEmployee && (
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  multiple
                  size="small"
                  options={employeeFilterOptions}
                  value={filters.employeeSearch}
                  onChange={(_, newValue) => setFilters((prev) => ({ ...prev, employeeSearch: newValue }))}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox size="small" checked={selected} style={{ marginRight: 4 }} />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Empleado" placeholder={filters.employeeSearch.length === 0 ? 'Todos' : ''} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
            )}
            {canFilterValidator && (
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  multiple
                  size="small"
                  options={validatorFilterOptions}
                  value={filters.validatorSearch}
                  onChange={(_, newValue) => setFilters((prev) => ({ ...prev, validatorSearch: newValue }))}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox size="small" checked={selected} style={{ marginRight: 4 }} />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Validador" placeholder={filters.validatorSearch.length === 0 ? 'Todos' : ''} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={canFilterEmployee && canFilterValidator ? 1.5 : 2}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  label="Estado"
                  onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, status: e.target.value }))}
                >
                  {filteredStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {activeTab === 'postventa' && (
              <Grid item xs={12} sm={6} md={canFilterEmployee && canFilterValidator ? 1.5 : 2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Incidencia</InputLabel>
                  <Select
                    value={filters.incidence}
                    label="Incidencia"
                    onChange={(e) => setFilters((previousFilters) => ({ ...previousFilters, incidence: e.target.value }))}
                  >
                    {INCIDENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Intervalo fecha extra"
                value={formatDateRangeLabel(filters.extraDateFrom, filters.extraDateTo)}
                placeholder="Seleccionar intervalo"
                onClick={(event) => setExtraDateRangeAnchorEl(event.currentTarget)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateIcon fontSize="small" sx={{ color: '#0284c7' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Intervalo fecha pago"
                value={formatDateRangeLabel(filters.paymentDateFrom, filters.paymentDateTo)}
                placeholder="Seleccionar intervalo"
                onClick={(event) => setPaymentDateRangeAnchorEl(event.currentTarget)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateIcon fontSize="small" sx={{ color: '#0284c7' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={canFilterEmployee && canFilterValidator ? 0.5 : 1} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Tooltip title="Resetear Filtros">
                <IconButton
                  onClick={() => {
                    setFilters(DEFAULT_FILTERS);
                  }}
                  sx={{
                    border: '1.5px solid #bae6fd',
                    bgcolor: '#f0f9ff',
                    color: '#0284c7',
                    '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                  }}
                >
                  <ResetIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de Resultados para Escritorio */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          bgcolor: '#ffffff',
          borderRadius: 3,
          border: '1.5px solid #38bdf8',
          boxShadow: '0 10px 25px -5px rgba(14, 165, 233, 0.08), 0 4px 10px -2px rgba(14, 165, 233, 0.04)',
          width: '100%',
          overflowX: 'auto',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 14px 28px -4px rgba(14, 165, 233, 0.12), 0 6px 14px -2px rgba(14, 165, 233, 0.06)',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: '#f0f9ff',
            borderBottom: '1.5px solid #bae6fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                px: 1.5,
                py: 0.4,
                borderRadius: '50px',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              Listado de Extras
            </Box>
            <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600 }}>
              {sortedRows.length} de {totalCount} registros encontrados
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {selectedRows.length > 0 && (
              <Chip
                size="small"
                label={`${selectedRows.length} seleccionados`}
                sx={{
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  border: '1px solid #7dd3fc',
                  fontWeight: 700,
                  borderRadius: '50px',
                }}
              />
            )}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.5,
                py: 0.4,
                borderRadius: '50px',
                bgcolor: '#ecfdf5',
                border: '1.2px solid #a7f3d0',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', textTransform: 'uppercase', fontSize: '0.675rem' }}>
                Teórico:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#065f46', fontSize: '0.85rem' }}>
                {formatCurrency(stats.sumTheoretical, activeTab === 'reparaciones')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.5,
                py: 0.4,
                borderRadius: '50px',
                bgcolor: '#ffffff',
                border: '1.2px solid #7dd3fc',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', fontSize: '0.675rem' }}>
                Final:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#0369a1', fontSize: '0.85rem' }}>
                {formatCurrency(stats.sumFinal, activeTab === 'reparaciones')}
              </Typography>
            </Box>

            {activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursFinal > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '50px',
                  bgcolor: '#f0fdf4',
                  border: '1.2px solid #86efac',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', fontSize: '0.675rem' }}>
                  Recorte de horas:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#15803d', fontSize: '0.85rem' }}>
                  {formatCurrency(summary.trimmedHoursFinal, true)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
        <Table size="small" sx={{ width: '100%', minWidth: { xs: 1100, lg: 'auto' }, tableLayout: { xs: 'auto', lg: 'fixed' } }}>
          <TableHead sx={{ bgcolor: '#f8fafc', borderBottom: '2px solid #bae6fd' }}>
            <TableRow>
              <TableCell padding="checkbox" sx={{ py: 1.5 }}>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && selectedRows.length > 0}
                  onChange={toggleSelectAllVisible}
                  sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#0284c7' }, '&.MuiCheckbox-indeterminate': { color: '#0284c7' } }}
                />
              </TableCell>
              {activeTab === 'postventa' ? (
                <>
                  <TableCell align="left" sortDirection={sortField === 'type' ? sortDirection : false} sx={{ fontWeight: 700, width: '18%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'type'}
                      direction={sortField === 'type' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('type')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Tipo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sortDirection={sortField === 'employee' ? sortDirection : false} sx={{ fontWeight: 700, width: '18%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'employee'}
                      direction={sortField === 'employee' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('employee')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Empleado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'extraDate' ? sortDirection : false} sx={{ fontWeight: 700, width: '7%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'extraDate'}
                      direction={sortField === 'extraDate' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('extraDate')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Fecha Extra
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'paymentDate' ? sortDirection : false} sx={{ fontWeight: 700, width: '7%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'paymentDate'}
                      direction={sortField === 'paymentDate' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('paymentDate')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Fecha Pago
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'theoreticalQuantity' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'theoreticalQuantity'}
                      direction={sortField === 'theoreticalQuantity' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('theoreticalQuantity')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Cant. Teórica
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'finalQuantity' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'finalQuantity'}
                      direction={sortField === 'finalQuantity' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('finalQuantity')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Cant. Final
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'status' ? sortDirection : false} sx={{ fontWeight: 700, width: '11%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('status')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Estado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'incidence' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'incidence'}
                      direction={sortField === 'incidence' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('incidence')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Incidencia
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sortDirection={sortField === 'comment' ? sortDirection : false} sx={{ fontWeight: 700, width: '11%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'comment'}
                      direction={sortField === 'comment' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('comment')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Comentario
                    </TableSortLabel>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell align="left" sortDirection={sortField === 'employee' ? sortDirection : false} sx={{ fontWeight: 700, width: '14%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'employee'}
                      direction={sortField === 'employee' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('employee')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Empleado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sortDirection={sortField === 'delegationName' ? sortDirection : false} sx={{ fontWeight: 700, width: '9%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'delegationName'}
                      direction={sortField === 'delegationName' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('delegationName')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Delegación OT
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'extraDate' ? sortDirection : false} sx={{ fontWeight: 700, width: '7%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'extraDate'}
                      direction={sortField === 'extraDate' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('extraDate')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Fecha Extra
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'shiftType' ? sortDirection : false} sx={{ fontWeight: 700, width: '6.5%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'shiftType'}
                      direction={sortField === 'shiftType' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('shiftType')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Turno
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'workOrder' ? sortDirection : false} sx={{ fontWeight: 700, width: '7.5%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'workOrder'}
                      direction={sortField === 'workOrder' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('workOrder')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      OT
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'agreedAmount' ? sortDirection : false} sx={{ fontWeight: 700, width: '7%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'agreedAmount'}
                      direction={sortField === 'agreedAmount' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('agreedAmount')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Imp. Pactado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'agreedHours' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'agreedHours'}
                      direction={sortField === 'agreedHours' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('agreedHours')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      H. Pactadas
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'performedHours' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'performedHours'}
                      direction={sortField === 'performedHours' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('performedHours')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      H. Realizadas
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'trimmedHours' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'trimmedHours'}
                      direction={sortField === 'trimmedHours' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('trimmedHours')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      H. Recortadas
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'theoreticalQuantity' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'theoreticalQuantity'}
                      direction={sortField === 'theoreticalQuantity' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('theoreticalQuantity')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Cant. Teórica
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'finalQuantity' ? sortDirection : false} sx={{ fontWeight: 700, width: '6%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'finalQuantity'}
                      direction={sortField === 'finalQuantity' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('finalQuantity')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Cant. Final
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortField === 'status' ? sortDirection : false} sx={{ fontWeight: 700, width: '9%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('status')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Estado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sortDirection={sortField === 'comment' ? sortDirection : false} sx={{ fontWeight: 700, width: '10%', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>
                    <TableSortLabel
                      active={sortField === 'comment'}
                      direction={sortField === 'comment' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('comment')}
                      sx={{ color: '#0369a1 !important', '& .MuiTableSortLabel-icon': { color: '#0284c7 !important' } }}
                    >
                      Comentario
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell align="center" sx={{ fontWeight: 700, width: activeTab === 'reparaciones' ? 160 : 150, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? sortedRows.map((row) => {
              const isSelected = selectedIds.includes(row.id);
              const finalQty = getDisplayedFinalQuantity(row);
              const originalQty = Number(row.theoreticalQuantity);
              const hasDiff = finalQty !== originalQty;
              const statusPresentation = getStatusPresentation(row);

              return (
              <TableRow
                key={row.id}
                hover
                sx={{
                  bgcolor: isSelected
                    ? 'rgba(14, 165, 233, 0.08) !important'
                    : hasDiff
                    ? 'rgba(254, 243, 199, 0.25)'
                    : '#ffffff',
                  borderLeft: isSelected
                    ? '4px solid #0284c7'
                    : hasDiff
                    ? '4px solid #f59e0b'
                    : '4px solid transparent',
                  transition: 'all 0.15s ease',
                  '& td': {
                    borderBottom: '1px solid #f1f5f9',
                    py: 1.2,
                  },
                  '&:hover': {
                    bgcolor: isSelected
                      ? 'rgba(14, 165, 233, 0.12) !important'
                      : 'rgba(14, 165, 233, 0.04) !important',
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleRowSelection(row.id)}
                    sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#0284c7' } }}
                  />
                </TableCell>
                {activeTab === 'postventa' ? (
                  <>
                    <TableCell align="left" sx={{ maxWidth: 0 }}>
                      {(() => {
                        const typeText = row.extraTypeDescription || row.extraType || '-';
                        const isExpanded = Boolean(expandedTypeRows[row.id]);

                        return (
                      <Stack spacing={0.5} alignItems="flex-start" sx={{ width: '100%', minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                          <Tooltip title={typeText} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: '0.75rem', lg: '0.8125rem' },
                                color: '#0f172a',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {typeText}
                            </Typography>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => toggleTypeExpanded(row.id)}
                            sx={{
                              flexShrink: 0,
                              color: '#0284c7',
                              p: 0.25,
                              '&:hover': { bgcolor: '#e0f2fe' },
                            }}
                          >
                            {isExpanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {String(row.loadingOrigin || '').toLowerCase() === 'manual' && (
                            <Chip
                              label="Manual"
                              size="small"
                              sx={{
                                height: 19,
                                fontSize: '0.675rem',
                                fontWeight: 700,
                                bgcolor: '#e0f2fe',
                                color: '#0369a1',
                                border: '1px solid #bae6fd',
                                borderRadius: '10px',
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="left" sx={{ maxWidth: { md: 160, lg: 200 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            bgcolor: '#e0f2fe',
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.675rem',
                            fontWeight: 700,
                            flexShrink: 0,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {getEmployeeInitials(row)}
                        </Box>
                        <Tooltip title={row.employeeName || '-'} arrow placement="top-start">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { md: '0.75rem', lg: '0.8125rem' },
                              color: '#1e293b',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'default',
                              display: 'block',
                              minWidth: 0,
                            }}
                          >
                            {formatEmployeeShortName(row)}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: { md: '0.75rem', lg: '0.8rem' }, color: '#334155', fontWeight: 500 }}>
                        {formatDate(row.extraDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: { md: '0.75rem', lg: '0.8rem' }, color: row.paymentDate ? '#334155' : '#94a3b8', fontWeight: 500 }}>
                        {formatDate(row.paymentDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: { md: '0.75rem', lg: '0.8125rem' }, color: '#64748b' }}>
                        {formatCurrency(row.theoreticalQuantity)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      {hasDiff ? (
                        <Tooltip title={`Modificado de ${formatCurrency(row.theoreticalQuantity)}`} arrow>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1,
                              py: 0.25,
                              borderRadius: '8px',
                              bgcolor: '#fef3c7',
                              border: '1px solid #fde68a',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#b45309',
                                lineHeight: 1.2,
                                fontSize: { md: '0.75rem', lg: '0.8125rem' },
                              }}
                            >
                              {formatCurrency(finalQty)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#0f172a',
                            lineHeight: 1.2,
                            fontSize: { md: '0.75rem', lg: '0.8125rem' },
                          }}
                        >
                          {formatCurrency(finalQty)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={statusPresentation.fullLabel} arrow>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            width: '100%',
                            maxWidth: 150,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: '50px',
                            bgcolor: statusPresentation.bgColor,
                            color: statusPresentation.textColor,
                            border: `1px solid ${statusPresentation.borderColor}`,
                            fontSize: { md: '0.675rem', lg: '0.72rem' },
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            textAlign: 'center',
                            lineHeight: 1.2,
                          }}
                        >
                          {row.status === 'validated' && <ValidateIcon sx={{ fontSize: 13 }} />}
                          <span>{statusPresentation.shortLabel}</span>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={row.incidenceErrorType || ''} disableHoverListener={!row.incidenceErrorType} arrow>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1,
                            py: 0.25,
                            borderRadius: '50px',
                            bgcolor: row.hasIncidence ? '#fef2f2' : '#f8fafc',
                            color: row.hasIncidence ? '#dc2626' : '#94a3b8',
                            border: row.hasIncidence ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            cursor: row.incidenceErrorType ? 'help' : 'default',
                          }}
                        >
                          {row.hasIncidence ? 'Sí' : 'No'}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left" sx={{ color: 'text.secondary', maxWidth: 0 }}>
                      {getDisplayedComment(row) ? (() => {
                        const displayedComment = getDisplayedComment(row);
                        const isExpanded = Boolean(expandedCommentRows[row.id]);
                        const canExpand = displayedComment.length > 32;

                        return (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                          <Tooltip title={displayedComment} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { md: '0.75rem', lg: '0.8rem' },
                                color: '#64748b',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {displayedComment}
                            </Typography>
                          </Tooltip>
                          {canExpand && (
                            <IconButton
                              size="small"
                              onClick={() => toggleCommentExpanded(row.id)}
                              sx={{ flexShrink: 0, color: '#0284c7', p: 0.25, '&:hover': { bgcolor: '#e0f2fe' } }}
                            >
                              {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          )}
                        </Stack>
                        );
                      })() : (
                        <Typography variant="body2" sx={{ color: '#cbd5e1', textAlign: 'center' }}>-</Typography>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell align="left" sx={{ maxWidth: { md: 160, lg: 200 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            bgcolor: '#e0f2fe',
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.675rem',
                            fontWeight: 700,
                            flexShrink: 0,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {getEmployeeInitials(row)}
                        </Box>
                        <Tooltip title={row.employeeName || '-'} arrow placement="top-start">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { md: '0.75rem', lg: '0.8125rem' },
                              color: '#1e293b',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'default',
                              display: 'block',
                              minWidth: 0,
                            }}
                          >
                            {formatEmployeeShortName(row)}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={row.delegationName || '-'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          bgcolor: '#f1f5f9',
                          color: '#334155',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: { md: '0.75rem', lg: '0.8rem' }, color: '#334155', fontWeight: 500 }}>
                        {formatDate(row.extraDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {renderShiftBadge(row.shiftType)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={row.workOrder ? `OT completa: ${row.workOrder}` : ''} arrow>
                        <Box
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            color: '#0284c7',
                            bgcolor: '#f0f9ff',
                            px: 1,
                            py: 0.3,
                            borderRadius: 1,
                            display: 'inline-block',
                            border: '1px solid #bae6fd',
                            cursor: 'default',
                          }}
                        >
                          {formatWorkOrder(row.workOrder)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { md: '0.75rem', lg: '0.8125rem' }, color: '#334155' }}>
                        {formatCurrency(row.agreedAmount, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: { md: '0.75rem', lg: '0.8rem' }, color: '#334155', fontWeight: 500 }}>
                        {Number(row.agreedHours ?? 0).toFixed(1)}h
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: { md: '0.75rem', lg: '0.8rem' }, color: '#334155', fontWeight: 500 }}>
                        {Number(row.performedHours ?? 0).toFixed(1)}h
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const trimmed = Number(row.trimmedHours ?? 0);
                        const isPositive = trimmed > 0;
                        const isNegative = trimmed < 0;
                        return (
                          <Chip
                            label={`${trimmed.toFixed(1)}h`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              bgcolor: isPositive ? '#fef3c7' : isNegative ? '#fee2e2' : '#f8fafc',
                              color: isPositive ? '#b45309' : isNegative ? '#dc2626' : '#64748b',
                              border: `1px solid ${isPositive ? '#fde68a' : isNegative ? '#fca5a5' : '#e2e8f0'}`,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: { md: '0.75rem', lg: '0.8125rem' }, color: '#64748b' }}>
                        {formatCurrency(row.theoreticalQuantity, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      {hasDiff ? (
                        <Tooltip title={`Modificado de ${formatCurrency(row.theoreticalQuantity, 2)}`} arrow>
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1,
                              py: 0.25,
                              borderRadius: '8px',
                              bgcolor: '#fef3c7',
                              border: '1px solid #fde68a',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#b45309',
                                lineHeight: 1.2,
                                fontSize: { md: '0.75rem', lg: '0.8125rem' },
                              }}
                            >
                              {formatCurrency(finalQty, 2)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#0f172a',
                            lineHeight: 1.2,
                            fontSize: { md: '0.75rem', lg: '0.8125rem' },
                          }}
                        >
                          {formatCurrency(finalQty, 2)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={statusPresentation.fullLabel} arrow>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            width: '100%',
                            maxWidth: 150,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: '50px',
                            bgcolor: statusPresentation.bgColor,
                            color: statusPresentation.textColor,
                            border: `1px solid ${statusPresentation.borderColor}`,
                            fontSize: { md: '0.675rem', lg: '0.72rem' },
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            textAlign: 'center',
                            lineHeight: 1.2,
                          }}
                        >
                          {row.status === 'validated' && <ValidateIcon sx={{ fontSize: 13 }} />}
                          <span>{statusPresentation.shortLabel}</span>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left" sx={{ color: 'text.secondary', maxWidth: 0 }}>
                      {getDisplayedComment(row) ? (() => {
                        const displayedComment = getDisplayedComment(row);
                        const isExpanded = Boolean(expandedCommentRows[row.id]);
                        const canExpand = displayedComment.length > 32;

                        return (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                          <Tooltip title={displayedComment} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { md: '0.75rem', lg: '0.8rem' },
                                color: '#64748b',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {displayedComment}
                            </Typography>
                          </Tooltip>
                          {canExpand && (
                            <IconButton
                              size="small"
                              onClick={() => toggleCommentExpanded(row.id)}
                              sx={{ flexShrink: 0, color: '#0284c7', p: 0.25, '&:hover': { bgcolor: '#e0f2fe' } }}
                            >
                              {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          )}
                        </Stack>
                        );
                      })() : (
                        <Typography variant="body2" sx={{ color: '#cbd5e1', textAlign: 'center' }}>-</Typography>
                      )}
                    </TableCell>
                  </>
                )}
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ whiteSpace: 'nowrap' }}>
                    {activeTab === 'reparaciones' && (
                      <Tooltip title="Añadir o editar anotación interna" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAnnotationRow(row);
                            setAnnotationText(row.comment || '');
                          }}
                          sx={{
                            color: '#0284c7',
                            p: 0.6,
                            borderRadius: '8px',
                            border: '1px solid #bae6fd',
                            bgcolor: '#f0f9ff',
                            '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                          }}
                        >
                          <CommentNoteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar importe final" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canEditAmount(row)}
                          onClick={() => startEditingRow(row)}
                          sx={{
                            color: '#0284c7',
                            p: 0.6,
                            borderRadius: '8px',
                            border: '1px solid #bae6fd',
                            bgcolor: '#f0f9ff',
                            '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                            '&.Mui-disabled': { borderColor: '#e2e8f0', bgcolor: 'transparent' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Ver validaciones" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setValidationsDialogRow(row)}
                        sx={{
                          color: '#0369a1',
                          p: 0.6,
                          borderRadius: '8px',
                          border: '1px solid #bae6fd',
                          bgcolor: '#f0f9ff',
                          '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                        }}
                      >
                        <RuleIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Validar extra" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={(!(hasFullAccessForActiveTab ? hasPendingValidation(row) : findPendingValidationForCurrentUser(row))) || validatingIds.includes(row.id)}
                          onClick={() => validateRows([row])}
                          sx={{
                            color: '#059669',
                            p: 0.6,
                            borderRadius: '8px',
                            border: '1px solid #a7f3d0',
                            bgcolor: '#ecfdf5',
                            '&:hover': { bgcolor: '#d1fae5', borderColor: '#6ee7b7' },
                            '&.Mui-disabled': { borderColor: '#e2e8f0', bgcolor: 'transparent' },
                          }}
                        >
                          <ValidateIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {activeTab === 'postventa' && (
                      <Tooltip title="Ver detalles heredados" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setMapLoading(true);
                            setDetailsDialogRow(row);
                          }}
                          sx={{
                            color: '#64748b',
                            p: 0.6,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#f8fafc',
                            '&:hover': { bgcolor: '#f1f5f9', color: '#334155' },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteExtra(row) && (
                      <Tooltip title="Eliminar extra manual" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setExtraToDelete(row)}
                          sx={{
                            color: '#dc2626',
                            p: 0.6,
                            borderRadius: '8px',
                            border: '1px solid #fecaca',
                            bgcolor: '#fef2f2',
                            '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              );
            }) : !(activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursTheoretical > 0) ? (
              <TableRow>
                <TableCell colSpan={activeTab === 'reparaciones' ? 15 : 11} align="center" sx={{ py: 10 }}>
                  <Box sx={{ opacity: 0.5 }}>
                    <SearchIcon sx={{ fontSize: 48, mb: 2, color: '#0284c7' }} />
                    <Typography variant="h6" sx={{ color: '#0f172a' }}>No se han encontrado registros</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Prueba a cambiar los filtros de búsqueda</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}

            {activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursTheoretical > 0 && (
              <TableRow
                sx={{
                  bgcolor: '#f0fdf4',
                  borderTop: '2px solid #86efac',
                  borderBottom: '2px solid #86efac',
                  '& td': {
                    py: 1.5,
                  },
                  '&:hover': {
                    bgcolor: '#dcfce7 !important',
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Total acumulado de comisión por recorte de horas">
                      <AccessTimeIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="left">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label="Recorte de horas"
                      sx={{
                        bgcolor: '#dcfce7',
                        color: '#15803d',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        border: '1px solid #86efac',
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534', fontSize: '0.8rem' }}>
                      Total acumulado mensual
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Varios"
                    sx={{ color: '#15803d', borderColor: '#86efac', fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#166534', fontSize: '0.85rem' }}>
                    {formatCurrency(summary.trimmedHoursTheoretical, true)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#15803d', fontSize: '0.85rem' }}>
                    {formatCurrency(summary.trimmedHoursFinal, true)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label="Consolidado"
                    sx={{
                      bgcolor: '#dcfce7',
                      color: '#15803d',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      height: 22,
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ color: '#64748b', fontSize: '0.78rem' }}>-</TableCell>
                <TableCell align="left">
                  <Typography variant="caption" sx={{ color: '#15803d', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    Total mensual por recorte de horas
                  </Typography>
                </TableCell>
                <TableCell align="center">-</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Vista de Tarjetas para Móviles */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
        {/* Resumen móvil compacto */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: '#f0f9ff',
            border: '1.5px solid #bae6fd',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#0369a1' }}>
            {sortedRows.length} de {totalCount} registros encontrados
          </Typography>
          <Stack direction="row" spacing={1}>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '50px',
                bgcolor: '#ecfdf5',
                border: '1px solid #a7f3d0',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#059669' }}>
                Teór:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#065f46' }}>
                {formatCurrency(stats.sumTheoretical, activeTab === 'reparaciones')}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '50px',
                bgcolor: '#ffffff',
                border: '1px solid #7dd3fc',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#0284c7' }}>
                Final:
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#0369a1' }}>
                {formatCurrency(stats.sumFinal, activeTab === 'reparaciones')}
              </Typography>
            </Box>
            {activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursFinal > 0 && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: '50px',
                  bgcolor: '#f0fdf4',
                  border: '1px solid #86efac',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#16a34a' }}>
                  Recorte:
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#15803d' }}>
                  {formatCurrency(summary.trimmedHoursFinal, true)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {sortedRows.length > 0 ? sortedRows.map((row) => {
          const statusPresentation = getStatusPresentation(row);
          const isExpanded = !!expandedCards[row.id];
          const finalQty = getDisplayedFinalQuantity(row);
          const originalQty = Number(row.theoreticalQuantity);
          const isSelected = selectedIds.includes(row.id);

          return (
            <Card 
              key={row.id} 
              sx={{ 
                borderRadius: 3, 
                boxShadow: '0 8px 25px -4px rgba(14, 165, 233, 0.08)',
                border: '1.5px solid',
                borderColor: isSelected ? '#0284c7' : (finalQty !== originalQty ? '#f59e0b' : '#38bdf8'),
                bgcolor: '#ffffff',
                transition: 'all 0.2s ease',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                {/* Cabecera de la Tarjeta */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <Checkbox 
                      size="small" 
                      checked={isSelected} 
                      onChange={() => toggleRowSelection(row.id)} 
                      sx={{ p: 0, color: '#94a3b8', '&.Mui-checked': { color: '#0284c7' } }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5, flexWrap: 'wrap' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.2,
                            borderRadius: '50px',
                            bgcolor: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            fontSize: '0.675rem',
                            fontWeight: 700,
                          }}
                        >
                          {activeTab === 'reparaciones'
                            ? (row.workOrder ? `OT: ${formatWorkOrder(row.workOrder)}` : 'REPARACIÓN')
                            : (String(row.loadingOrigin || '').toLowerCase() === 'manual' ? 'MANUAL' : (row.extraType || 'EXTRA'))}
                        </Box>
                        {activeTab === 'reparaciones' && row.shiftType && renderShiftBadge(row.shiftType)}
                      </Box>
                      <Tooltip title={row.employeeName || '-'} arrow placement="top-start">
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                          {formatEmployeeShortName(row)}
                        </Typography>
                      </Tooltip>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.25 }}>
                        {activeTab === 'reparaciones'
                          ? (row.delegationName ? `Delegación: ${row.delegationName}` : 'Sin delegación')
                          : (row.extraTypeDescription || row.extraType || '-')}
                      </Typography>
                    </Box>
                  </Stack>
                  <Tooltip title={statusPresentation.fullLabel} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '50px',
                        bgcolor: statusPresentation.bgColor,
                        color: statusPresentation.textColor,
                        border: `1px solid ${statusPresentation.borderColor}`,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.status === 'validated' && <ValidateIcon sx={{ fontSize: 13 }} />}
                      {statusPresentation.shortLabel}
                    </Box>
                  </Tooltip>
                </Box>

                {/* Secciones de Información al estilo del lote navideño */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {activeTab === 'reparaciones' && (
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: '#e0f2fe',
                          color: '#0369a1',
                          fontSize: '0.675rem',
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}
                      >
                        HORAS Y PACTADO
                      </Box>
                      <Stack spacing={0.3} sx={{ pl: 1, fontSize: '0.825rem', color: '#334155' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                          • Imp. Pactado: <b>{formatCurrency(row.agreedAmount, 2)}</b>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                          • H. Pactadas: <b>{Number(row.agreedHours ?? 0).toFixed(1)}h</b> | Realizadas: <b>{Number(row.performedHours ?? 0).toFixed(1)}h</b>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                          • H. Recortadas: <b>{Number(row.trimmedHours ?? 0).toFixed(1)}h</b>
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  {/* Píldora de Fechas */}
                  <Box>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.2,
                        borderRadius: '6px',
                        bgcolor: '#e0f2fe',
                        color: '#0369a1',
                        fontSize: '0.675rem',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        mb: 0.75,
                      }}
                    >
                      FECHAS
                    </Box>
                    <Stack spacing={0.3} sx={{ pl: 1, fontSize: '0.825rem', color: '#334155' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                        • Fecha Extra: <b>{formatDate(row.extraDate)}</b>
                      </Typography>
                      {activeTab === 'reparaciones' && row.shiftType && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 0.2 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            • Turno:
                          </Typography>
                          {renderShiftBadge(row.shiftType)}
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                        • Fecha Pago: <b>{formatDate(row.paymentDate)}</b>
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Píldora de Importes */}
                  <Box>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.2,
                        borderRadius: '6px',
                        bgcolor: '#e0f2fe',
                        color: '#0369a1',
                        fontSize: '0.675rem',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        mb: 0.75,
                      }}
                    >
                      IMPORTES
                    </Box>
                    <Stack spacing={0.3} sx={{ pl: 1, fontSize: '0.825rem', color: '#334155' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                        • Cant. Teórica: <b>{formatCurrency(row.theoreticalQuantity, activeTab === 'reparaciones')}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                        • Cant. Final:{' '}
                        <b style={{ color: finalQty !== originalQty ? '#b45309' : '#0f172a' }}>
                          {formatCurrency(finalQty, activeTab === 'reparaciones')}
                        </b>
                      </Typography>
                    </Stack>
                  </Box>

                  {row.comment && (
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: '#e0f2fe',
                          color: '#0369a1',
                          fontSize: '0.675rem',
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}
                      >
                        COMENTARIO
                      </Box>
                      <Typography variant="body2" sx={{ pl: 1, fontSize: '0.825rem', color: '#475569', fontStyle: 'italic' }}>
                        • {row.comment}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Botón inferior al estilo: "¡Este es tu lote seleccionado!" */}
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                  <Button
                    size="small"
                    onClick={() => toggleCardExpanded(row.id)}
                    sx={{
                      borderRadius: '50px',
                      bgcolor: '#e0f2fe',
                      border: '1.5px solid #38bdf8',
                      color: '#0369a1',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      px: 2.5,
                      py: 0.6,
                      width: '100%',
                      '&:hover': {
                        bgcolor: '#bae6fd',
                        borderColor: '#0284c7',
                      },
                    }}
                    endIcon={isExpanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
                  >
                    {isExpanded ? 'Ver menos detalles' : 'Ver todos los detalles'}
                  </Button>
                </Box>

                {/* Contenido Desplegable */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0f2fe' }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700 }} display="block">Cant. Teórica</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.theoreticalQuantity, activeTab === 'reparaciones')}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700 }} display="block">Cant. Final</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: finalQty !== originalQty ? '#b45309' : 'inherit' }}>{formatCurrency(finalQty, activeTab === 'reparaciones')}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700 }} display="block">Fecha Pago</Typography>
                        <Typography variant="body2">{formatDate(row.paymentDate)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700 }} display="block">Origen</Typography>
                        <Typography variant="body2">{row.loadingOrigin || '-'}</Typography>
                      </Grid>
                      {row.hasIncidence && row.incidenceErrorType && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="error.main" display="block" sx={{ fontWeight: 'bold' }}>Detalle de Incidencia</Typography>
                          <Typography variant="body2" color="error.main">{row.incidenceErrorType}</Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Collapsible buttons in mobile */}
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {row.details && row.details.length > 0 && (
                        <Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => toggleShowDetails(row.id)}
                            endIcon={showDetails[row.id] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            sx={{ textTransform: 'none', width: '100%', justifyContent: 'space-between', borderRadius: '50px', borderColor: '#bae6fd', color: '#0369a1' }}
                          >
                            Detalles Heredados ({row.details.length})
                          </Button>
                          <Collapse in={!!showDetails[row.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #bae6fd' }}>
                                <Table size="small">
                                  <TableBody>
                                    {row.details.map((detail) => (
                                      <TableRow key={`${detail.label}-${detail.value}`}>
                                        <TableCell sx={{ fontWeight: 600, py: 0.5, fontSize: '0.75rem', color: '#0369a1' }}>{detail.label}</TableCell>
                                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{renderDetailValue(detail.value)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => toggleShowValidations(row.id)}
                          endIcon={showValidations[row.id] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          sx={{ textTransform: 'none', width: '100%', justifyContent: 'space-between', borderRadius: '50px', borderColor: '#bae6fd', color: '#0369a1' }}
                        >
                          Historial de Validaciones ({(row.validations || []).length})
                        </Button>
                        <Collapse in={!!showValidations[row.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1 }}>
                            {(row.validations || []).length > 0 ? (
                              <Stack spacing={1}>
                                {row.validations.map((validation) => (
                                  <Box 
                                    key={validation.id} 
                                    sx={{ 
                                      p: 1.5, 
                                      borderRadius: 2, 
                                      border: '1px solid', 
                                      borderColor: validation.status === 'pending' ? '#bae6fd' : '#e2e8f0',
                                      bgcolor: validation.status === 'pending' ? '#f0f9ff' : '#ffffff'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#0f172a' }}>
                                        {validation.validatorName || `Validador ${validation.validationPosition}`}
                                      </Typography>
                                      <Chip 
                                        label={getValidationStatusLabel(validation.status)} 
                                        size="small" 
                                        color={
                                          validation.status === 'approved' ? 'success' :
                                          validation.status === 'rejected' ? 'error' :
                                          validation.status === 'pending' ? 'warning' : 'default'
                                        }
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem', borderRadius: '50px' }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {validation.validationDate ? formatDate(validation.validationDate, true) : '-'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: '#0284c7' }}>
                                        Propuesto: {validation.validationAmount !== null ? formatCurrency(validation.validationAmount, activeTab === 'reparaciones') : '-'}
                                      </Typography>
                                    </Box>
                                    {validation.comment && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.7rem' }}>
                                        "{validation.comment}"
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>No hay validaciones registradas.</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.5, borderColor: '#e0f2fe' }} />

                    {/* Acciones de la Tarjeta */}
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                      {activeTab === 'reparaciones' && (
                        <Tooltip title="Añadir o editar anotación interna">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAnnotationRow(row);
                              setAnnotationText(row.comment || '');
                            }}
                            sx={{ border: '1px solid #bae6fd', bgcolor: '#f0f9ff', color: '#0284c7' }}
                          >
                            <CommentNoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar importe final">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canEditAmount(row)}
                            onClick={() => startEditingRow(row)}
                            sx={{ border: '1px solid #bae6fd', bgcolor: '#f0f9ff', color: '#0284c7' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Validar extra">
                        <span>
                          <IconButton
                            size="small"
                            disabled={(!(hasFullAccessForActiveTab ? hasPendingValidation(row) : findPendingValidationForCurrentUser(row))) || validatingIds.includes(row.id)}
                            onClick={() => validateRows([row])}
                            sx={{ border: '1px solid #a7f3d0', bgcolor: '#ecfdf5', color: '#059669' }}
                          >
                            <ValidateIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canDeleteExtra(row) && (
                        <Tooltip title="Eliminar extra manual">
                          <IconButton
                            size="small"
                            onClick={() => setExtraToDelete(row)}
                            sx={{ border: '1px solid #fecaca', bgcolor: '#fef2f2', color: '#dc2626' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        }) : !(activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursTheoretical > 0) ? (
          <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
            <SearchIcon sx={{ fontSize: 48, mb: 1, color: '#0284c7' }} />
            <Typography variant="subtitle1" sx={{ color: '#0f172a' }}>No se han encontrado registros</Typography>
          </Box>
        ) : null}

        {activeTab === 'reparaciones' && isOperarioOnly && summary.trimmedHoursTheoretical > 0 && (
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: '#f0fdf4',
              border: '1.5px solid #86efac',
              boxShadow: '0 4px 15px -2px rgba(22, 163, 74, 0.1)',
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Avatar sx={{ bgcolor: '#dcfce7', color: '#16a34a', width: 38, height: 38 }}>
                  <AccessTimeIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#166534' }}>
                    Recorte de horas (Acumulado)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#15803d' }}>
                    Total mensual por recorte de horas
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700 }} display="block">Cant. Teórica</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#166534' }}>{formatCurrency(summary.trimmedHoursTheoretical, true)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700 }} display="block">Cant. Final</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#15803d' }}>{formatCurrency(summary.trimmedHoursFinal, true)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Indicador inferior: si hay más registros, la tarjeta SIEMPRE está presente al llegar al final */}
      {hasMore && (
        <Card
          ref={loadMoreRef}
          elevation={0}
          sx={{
            my: 3,
            mb: { xs: 12, md: 15 },
            p: 3,
            borderRadius: 3,
            bgcolor: 'rgba(240, 249, 255, 0.95)',
            border: '2px dashed #0284c7',
            boxShadow: '0 8px 24px -4px rgba(2, 132, 199, 0.15)',
            textAlign: 'center',
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress size={44} thickness={4.5} sx={{ color: '#0284c7', my: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0369a1', letterSpacing: '-0.01em' }}>
              {loadingMore ? t('ui.loading_more', 'Cargando más registros...') : 'Cargando siguientes registros...'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              {loadingMore
                ? `Obteniendo 100 registros adicionales (${rows.length} de ${totalCount} cargados)`
                : `Mostrando ${rows.length} de ${totalCount} registros`}
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 380, mt: 1 }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#bae6fd',
                  '& .MuiLinearProgress-bar': { bgcolor: '#0284c7' },
                }}
              />
            </Box>
          </Stack>
        </Card>
      )}

      {/* Si NO hay más registros, mensaje CLARO de fin de lista solo si se ha paginado/recargado con scroll */}
      {!hasMore && rows.length > 0 && (hasLoadedMore || rows.length > limit) && (
        <Card
          elevation={0}
          sx={{
            my: 3,
            mb: { xs: 12, md: 15 },
            p: 3,
            borderRadius: 3,
            bgcolor: '#f0fdf4',
            border: '2px solid #86efac',
            boxShadow: '0 4px 16px -2px rgba(22, 163, 74, 0.1)',
            textAlign: 'center',
          }}
        >
          <Stack alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.2)',
              }}
            >
              <CheckIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#166534' }}>
              Has llegado al final del listado
            </Typography>
            <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 600 }}>
              No hay más registros disponibles · Mostrando todos los {rows.length} de {totalCount} extras
            </Typography>
          </Stack>
        </Card>
      )}

      <Popover
        open={extraDateRangePickerOpen}
        anchorEl={extraDateRangeAnchorEl}
        onClose={() => setExtraDateRangeAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack spacing={2} sx={{ p: 2, minWidth: 320 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Fecha extra desde
              </Typography>
              <DateCalendar
                value={filters.extraDateFrom ? dayjs(filters.extraDateFrom) : null}
                onChange={(value) => handleDateRangeChange('extraDateFrom', value)}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Fecha extra hasta
              </Typography>
              <DateCalendar
                value={filters.extraDateTo ? dayjs(filters.extraDateTo) : null}
                onChange={(value) => handleDateRangeChange('extraDateTo', value)}
              />
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => {
                setFilters((previousFilters) => ({
                  ...previousFilters,
                  extraDateFrom: '',
                  extraDateTo: '',
                }));
              }}
            >
              Limpiar
            </Button>
            <Button onClick={() => setExtraDateRangeAnchorEl(null)}>Cerrar</Button>
          </Stack>
        </Stack>
      </Popover>

      <Popover
        open={paymentDateRangePickerOpen}
        anchorEl={paymentDateRangeAnchorEl}
        onClose={() => setPaymentDateRangeAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack spacing={2} sx={{ p: 2, minWidth: 320 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Fecha pago desde
              </Typography>
              <DateCalendar
                value={filters.paymentDateFrom ? dayjs(filters.paymentDateFrom) : null}
                onChange={(value) => handleDateRangeChange('paymentDateFrom', value)}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Fecha pago hasta
              </Typography>
              <DateCalendar
                value={filters.paymentDateTo ? dayjs(filters.paymentDateTo) : null}
                onChange={(value) => handleDateRangeChange('paymentDateTo', value)}
              />
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => {
                setFilters((previousFilters) => ({
                  ...previousFilters,
                  paymentDateFrom: '',
                  paymentDateTo: '',
                }));
              }}
            >
              Limpiar
            </Button>
            <Button onClick={() => setPaymentDateRangeAnchorEl(null)}>Cerrar</Button>
          </Stack>
        </Stack>
      </Popover>

      <Dialog open={Boolean(createDialogOpen && canManageManualExtras)} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Añadir extra manual</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Autocomplete
              options={users}
              loading={loadingUsers}
              value={createForm.user}
              onChange={(_, value) => {
                setCreateForm((previous) => ({
                  ...previous,
                  user: value,
                  delegation: null,
                  extraConfig: null,
                  amount: '',
                }));
              }}
              getOptionLabel={(option) => option?.fullName || option?.email || ''}
              renderInput={(params) => <TextField {...params} label="Empleado" required />}
            />
            <Autocomplete
              options={uniqueDelegations}
              value={createForm.delegation}
              onChange={(_, value) => {
                setCreateForm((previous) => ({
                  ...previous,
                  delegation: value,
                  extraConfig: null,
                  amount: '',
                }));
              }}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Delegación" required />}
              disabled={!createForm.user}
            />
            <Autocomplete
              options={filteredExtraConfigsForCreation}
              loading={loadingExtraConfigs}
              value={createForm.extraConfig}
              onChange={(_, value) => {
                const isManual = (value?.extraTypeName || '').toLowerCase().trim() === 'extra manual' || value?.extraTypeId === 61;
                setCreateForm((previous) => ({
                  ...previous,
                  extraConfig: value,
                  amount: value ? (isManual ? '' : String(value.amount ?? '')) : '',
                }));
              }}
              getOptionLabel={(option) => {
                const isManual = (option?.extraTypeName || '').toLowerCase().trim() === 'extra manual' || option?.extraTypeId === 61;
                return isManual
                  ? `${option?.extraTypeName || ''} (Manual)`
                  : `${option?.extraTypeName || ''} (${option?.amount ?? 0}€)`;
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Tipo de Extra" 
                  placeholder={loadingExtraConfigs ? "Cargando extras..." : (!createForm.delegation ? "Selecciona primero una delegación" : "Selecciona un tipo de extra")} 
                  required 
                />
              )}
              disabled={!createForm.delegation}
            />
            {createForm.extraConfig && (
              <TextField
                label={isExtraManualSelected ? "Importe (€) (Obligatorio)" : "Importe (€)"}
                type="number"
                value={createForm.amount}
                onChange={(e) => setCreateForm((previous) => ({ ...previous, amount: e.target.value }))}
                inputProps={{ step: '0.01', min: '0' }}
                fullWidth
                required={isExtraManualSelected}
                error={isExtraManualSelected && (createForm.amount === '' || isNaN(Number(createForm.amount)) || Number(createForm.amount) <= 0)}
                helperText={
                  isExtraManualSelected
                    ? 'Obligatorio: indica el importe a abonar para este extra manual'
                    : 'Importe según configuración (puedes ajustarlo si procede)'
                }
              />
            )}
            <TextField
              label="Fecha del extra"
              type="date"
              value={createForm.extraDate}
              onChange={(e) => setCreateForm((previous) => ({ ...previous, extraDate: e.target.value }))}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={isExtraManualSelected ? "Descripción del porqué (Obligatorio)" : "Comentario"}
              value={createForm.comment}
              onChange={(e) => setCreateForm((previous) => ({ ...previous, comment: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
              required={isExtraManualSelected}
              error={isExtraManualSelected && !createForm.comment?.trim()}
              placeholder={isExtraManualSelected ? "Describe el porqué de este extra manual..." : "Comentario opcional para este extra manual..."}
              helperText={isExtraManualSelected ? "Obligatorio para Extra Manual: indica el motivo o justificación" : "Opcional"}
            />
            {createForm.extraConfig && (
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(25, 118, 210, 0.04)', 
                  borderRadius: 2, 
                  border: '1px dashed rgba(25, 118, 210, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Detalles del Extra Seleccionado
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Importe:</strong> {createForm.amount !== '' && !isNaN(Number(createForm.amount)) ? formatCurrency(Number(createForm.amount)) : formatCurrency(createForm.extraConfig.amount)}
                </Typography>
                <Typography variant="body2">
                  <strong>Delegación:</strong> {createForm.extraConfig.delegationName || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Nombre del Extra:</strong> {createForm.extraConfig.extraTypeName || '-'}
                </Typography>
              </Box>
            )}
            {createForm.extraConfig && createForm.extraConfig.className && (
              (() => {
                const isPreventive = createForm.extraConfig.className.includes('AfterSalesPreventiveExtra');
                const isWarning = createForm.extraConfig.className.includes('AfterSalesWarningExtra');

                if (!isPreventive && !isWarning) {
                  return null;
                }

                return (
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(255, 152, 0, 0.03)', 
                      borderRadius: 2, 
                      border: '1px solid rgba(255, 152, 0, 0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}
                  >
                    <Typography variant="caption" align="center" sx={{ fontWeight: 'bold', color: 'warning.main', letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', width: '100%' }}>
                      Información Adicional del Subtipo
                    </Typography>

                    {isPreventive && (
                      <>
                        <TextField
                          label="Número de Parte"
                          value={createForm.numParte}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, numParte: e.target.value }))}
                          required
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Inicio de Engrase"
                          type="datetime-local"
                          value={createForm.inicioEngrase}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, inicioEngrase: e.target.value }))}
                          required
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Fin de Engrase"
                          type="datetime-local"
                          value={createForm.finEngrase}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, finEngrase: e.target.value }))}
                          required
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Checkbox 
                            size="small" 
                            checked={Boolean(createForm.trabajoFueraDeJornada)} 
                            onChange={(e) => setCreateForm(prev => ({ ...prev, trabajoFueraDeJornada: e.target.checked }))} 
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            Trabajo Fuera de Jornada
                          </Typography>
                        </Stack>
                      </>
                    )}

                    {isWarning && (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Número de Aviso"
                              value={createForm.warningNumber}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, warningNumber: e.target.value }))}
                              required
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Inicio de Aviso"
                              type="datetime-local"
                              value={createForm.warningStartDate}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, warningStartDate: e.target.value }))}
                              required
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Fin de Aviso"
                              type="datetime-local"
                              value={createForm.warningEndDate}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, warningEndDate: e.target.value }))}
                              required
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Latitud"
                              type="number"
                              value={createForm.latEnd}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, latEnd: e.target.value }))}
                              required
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Longitud"
                              type="number"
                              value={createForm.lonEnd}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, lonEnd: e.target.value }))}
                              required
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Motivo del Aviso"
                              value={createForm.warningReason}
                              onChange={(e) => setCreateForm(prev => ({ ...prev, warningReason: e.target.value }))}
                              required
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                            />
                          </Grid>
                        </Grid>

                        <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', pt: 1 }}>
                          <Typography variant="caption" align="center" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', width: '100%' }}>
                            Opciones del Aviso:
                          </Typography>
                          <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            {[
                              { key: 'vip', label: 'VIP' },
                              { key: 'accidente', label: 'Accidente' },
                              { key: 'rescate', label: 'Rescate' },
                              { key: 'urgente', label: 'Urgente' },
                              { key: 'incidencia', label: 'Incidencia' },
                              { key: 'parado', label: 'Parado' }
                            ].map((item) => (
                              <Grid item xs={4} key={item.key}>
                                <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
                                  <Checkbox 
                                    size="small" 
                                    checked={Boolean(createForm[item.key])} 
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, [item.key]: e.target.checked }))} 
                                  />
                                  <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>{item.label}</Typography>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })()
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetCreateForm();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateExtra}
            disabled={
              submittingCreate ||
              !createForm.delegation ||
              !createForm.user ||
              !createForm.extraConfig ||
              !createForm.extraDate ||
              (isExtraManualSelected && (!createForm.comment?.trim() || createForm.amount === '' || Number(createForm.amount) <= 0))
            }
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(extraToDelete)} onClose={() => !deletingExtra && setExtraToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#dc2626' }}>
          <DeleteIcon color="error" />
          Eliminar extra manual
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
            ¿Estás seguro de que deseas eliminar este extra manual? Esta acción no se puede deshacer.
          </Typography>
          {extraToDelete && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                <strong>Empleado:</strong> {extraToDelete.employeeName || '-'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                <strong>Tipo:</strong> {extraToDelete.extraType || extraToDelete.comment || '-'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                <strong>Fecha:</strong> {formatDate(extraToDelete.extraDate)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                <strong>Importe:</strong> {formatCurrency(extraToDelete.finalQuantity)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtraToDelete(null)} disabled={deletingExtra} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteExtra}
            disabled={deletingExtra}
          >
            {deletingExtra ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingRow)} onClose={() => setEditingRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Editar importe final</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Importe original: {formatCurrency(editingRow?.theoreticalQuantity || 0, activeTab === 'reparaciones')}
            </Alert>
            <TextField
              label="Nuevo importe final"
              type="number"
              value={editingRow?.finalQuantity || ''}
              onChange={(event) => {
                const nextValue = event.target.value;
                setEditingRow((previous) => previous ? {
                  ...previous,
                  finalQuantity: nextValue,
                } : previous);
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Comentario del cambio"
              value={editingRow?.comment || ''}
              onChange={(event) => {
                const nextValue = event.target.value;
                setEditingRow((previous) => previous ? {
                  ...previous,
                  comment: nextValue,
                } : previous);
              }}
              multiline
              minRows={3}
              required
              helperText="Obligatorio para registrar el motivo del cambio"
            />
            <Alert severity="warning">Nuevo importe a guardar: {formatCurrency(editingRow?.finalQuantity || 0, activeTab === 'reparaciones')}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRow(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEditedRow} disabled={savingEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Anotación interna */}
      <Dialog
        open={Boolean(annotationRow)}
        onClose={() => setAnnotationRow(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, border: '1.5px solid #38bdf8' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, color: '#0369a1', bgcolor: '#f0f9ff', borderBottom: '1px solid #bae6fd', py: 2 }}>
          Anotación del Extra {annotationRow?.workOrder ? `(OT ${formatWorkOrder(annotationRow.workOrder)})` : ''}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2, mt: 1 }}>
            Introduce o modifica la anotación/comentario interno para este extra:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            placeholder="Escribe una anotación o comentario..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setAnnotationRow(null)} sx={{ color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveAnnotation}
            disabled={savingAnnotation}
            sx={{
              bgcolor: '#0284c7',
              borderRadius: 2,
              px: 3,
              '&:hover': { bgcolor: '#0369a1' },
            }}
          >
            Guardar anotación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(validationsDialogRow)} onClose={() => setValidationsDialogRow(null)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, border: '1.5px solid #38bdf8' } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, color: '#0369a1', bgcolor: '#f0f9ff', borderBottom: '1px solid #bae6fd', py: 2 }}>
          Validaciones de la extra
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #bae6fd', overflow: 'hidden', mt: 1 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Orden</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Validador</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Importe original</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Importe propuesto</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Comentario</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem' }}>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(validationsDialogRow?.validations || []).length > 0 ? (validationsDialogRow.validations || []).map((validation) => (
                  <TableRow key={validation.id} hover sx={{ '&:hover': { bgcolor: '#f0f9ff' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{validation.validationPosition}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{validation.validatorName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getValidationStatusLabel(validation.status)}
                        size="small"
                        color={
                          validation.status === 'approved' ? 'success' :
                          validation.status === 'rejected' ? 'error' :
                          validation.status === 'pending' ? 'warning' : 'default'
                        }
                        variant="outlined"
                        sx={{ borderRadius: '50px', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#64748b' }}>{formatCurrency(validationsDialogRow?.theoreticalQuantity ?? 0, activeTab === 'reparaciones')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0284c7' }}>{validation.validationAmount !== null ? formatCurrency(validation.validationAmount, activeTab === 'reparaciones') : '-'}</TableCell>
                    <TableCell sx={{ color: '#475569', fontStyle: validation.comment ? 'italic' : 'normal' }}>{validation.comment || '-'}</TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: '0.75rem' }}>{formatDate(validation.validationDate, true)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#64748b' }}>No hay validaciones registradas.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setValidationsDialogRow(null)} sx={{ borderRadius: '50px', textTransform: 'none', px: 3 }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {(() => {
        const latVal = detailsDialogRow?.details?.find((d) => d.label?.toLowerCase() === 'latitud')?.value;
        const lonVal = detailsDialogRow?.details?.find((d) => d.label?.toLowerCase() === 'longitud')?.value;
        const lat = latVal ? parseFloat(latVal) : null;
        const lon = lonVal ? parseFloat(lonVal) : null;
        const hasCoordinates = lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0);

        const displayedDetails = (detailsDialogRow?.details || []).filter(
          (detail) => !hasCoordinates || !['latitud', 'longitud'].includes(detail.label?.toLowerCase())
        );

        return (
          <Dialog
            open={Boolean(detailsDialogRow)}
            onClose={() => setDetailsDialogRow(null)}
            fullWidth
            maxWidth={hasCoordinates ? 'md' : 'sm'}
            PaperProps={{ sx: { borderRadius: 3, border: '1.5px solid #38bdf8' } }}
          >
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, color: '#0369a1', bgcolor: '#f0f9ff', borderBottom: '1px solid #bae6fd', py: 1.8 }}>
              Detalles heredados de aftersale
            </DialogTitle>
            <DialogContent sx={{ pt: 2, pb: 1 }}>
              <Grid container spacing={2} sx={{ mt: 0.1 }}>
                <Grid item xs={12} md={hasCoordinates ? 6 : 12}>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #bae6fd', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem', py: 0.8 }}>Campo</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.72rem', py: 0.8 }}>Valor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedDetails.length > 0 ? displayedDetails.map((detail) => (
                          <TableRow key={`${detail.label}-${detail.value}`} hover sx={{ '&:hover': { bgcolor: '#f0f9ff' } }}>
                            <TableCell sx={{ fontWeight: 600, color: '#0369a1', py: 0.6, fontSize: '0.8rem' }}>{detail.label}</TableCell>
                            <TableCell sx={{ color: '#0f172a', py: 0.6, fontSize: '0.8rem' }}>{renderDetailValue(detail.value)}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 3, color: '#64748b' }}>No hay detalle heredado disponible.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {hasCoordinates && (
                  <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                    <Box sx={{ width: '100%', height: '100%', borderRadius: 2, border: '1px solid #bae6fd', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.8,
                          bgcolor: '#f0f9ff',
                          borderBottom: '1px solid #bae6fd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#0369a1',
                            fontSize: '0.78rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 17, color: '#0284c7' }} />
                          Ubicación ({lat}, {lon})
                        </Typography>
                        <Button
                          size="small"
                          href={getGoogleMapsExternalUrl(lat, lon)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: '#0284c7',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                          }}
                        >
                          Abrir en Google Maps ↗
                        </Button>
                      </Box>
                      <Box sx={{ position: 'relative', flex: 1, minHeight: 320, width: '100%', bgcolor: '#f8fafc' }}>
                        {mapLoading && (
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, bgcolor: '#f8fafc' }}>
                            <CircularProgress size={30} sx={{ color: '#0284c7' }} />
                          </Box>
                        )}
                        <iframe
                          title="Ubicación del aviso"
                          width="100%"
                          height="100%"
                          style={{ border: 0, display: 'block', minHeight: 320 }}
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                          onLoad={() => setMapLoading(false)}
                          src={getGoogleMapsEmbedUrl(lat, lon)}
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setDetailsDialogRow(null)} sx={{ borderRadius: '50px', textTransform: 'none', px: 3 }}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        );
      })()}


      <Dialog open={humanResourcesValidationModalOpen} onClose={() => setHumanResourcesValidationModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Validación restringida</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Para validar extras con permiso de administrador debes pertenecer a la estructura de RRHH en el organigrama GENERAL.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHumanResourcesValidationModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Informativo Tipos de Extra */}
      <Dialog open={extraTypeInfoOpen} onClose={() => setExtraTypeInfoOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, border: '1.5px solid #38bdf8' } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, color: '#0369a1', bgcolor: '#f0f9ff', borderBottom: '1px solid #bae6fd', py: 2 }}>
          Tipos de Extra
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {loadingExtraTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por delegación, descripción o precio"
                value={extraTypeConfigFilter}
                onChange={(event) => setExtraTypeConfigFilter(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#0284c7' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2, mt: 1 }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #bae6fd', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.75rem' }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.75rem' }}>Delegación</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', fontSize: '0.75rem' }}>Precio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredExtraTypeConfigs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#64748b' }}>No hay resultados para el filtro aplicado</TableCell>
                      </TableRow>
                    ) : (
                      filteredExtraTypeConfigs.map((row, idx) => (
                        <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f0f9ff' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{row.description}</TableCell>
                          <TableCell sx={{ color: '#475569' }}>{row.delegation}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#0284c7' }}>{row.price}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setExtraTypeInfoOpen(false)} sx={{ borderRadius: '50px', textTransform: 'none', px: 3 }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Paper
        elevation={0}
        sx={{
          position: { xs: 'static', md: 'sticky' },
          left: 0,
          right: 0,
          bottom: 16,
          zIndex: 2,
          borderRadius: 3,
          px: 2.5,
          py: 1.8,
          mt: { xs: 3, md: 0 },
          bgcolor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(10px)',
          border: '1.5px solid #38bdf8',
          boxShadow: '0 10px 30px -5px rgba(14, 165, 233, 0.15)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Box
                sx={{
                  bgcolor: '#0284c7',
                  color: '#ffffff',
                  px: 1.8,
                  py: 0.5,
                  borderRadius: '50px',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.02em',
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ValidateIcon sx={{ fontSize: 16 }} />
                Seleccionadas: {selectedRows.length}
              </Box>
              <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>
                Acciones rápidas
              </Typography>
            </Stack>
            {activeTab === 'postventa' && (
              <Button
                size="small"
                variant="outlined"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  borderRadius: '50px',
                  borderColor: '#bae6fd',
                  color: '#0369a1',
                  bgcolor: '#f0f9ff',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                }}
                onClick={handleOpenExtraTypeInfo}
              >
                Mostrar tabla de precios
              </Button>
            )}
          </Stack>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1} 
            flexWrap="wrap" 
            useFlexGap 
            sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'center' }}
          >
            {canManageManualExtras && activeTab === 'postventa' && (
              <Button 
                size="small" 
                variant="outlined" 
                startIcon={<AddIcon />} 
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: '50px',
                  borderColor: '#bae6fd',
                  color: '#0369a1',
                  bgcolor: '#f0f9ff',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                }}
              >
                Añadir manual
              </Button>
            )}
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<ValidateAllIcon />} 
              onClick={() => validateRows(sortedRows)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                borderRadius: '50px',
                borderColor: '#bae6fd',
                color: '#0369a1',
                bgcolor: '#f0f9ff',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
              }}
            >
              Validar todos
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ValidateIcon />}
              disabled={selectedRows.length === 0 || checkingHumanResourcesAccess}
              onClick={() => validateRows(selectedRows)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                borderRadius: '50px',
                bgcolor: '#0284c7',
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)',
                '&:hover': { bgcolor: '#0369a1' },
              }}
            >
              Validar marcados
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={exportingMatrix ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exportingMatrix}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                borderRadius: '50px',
                borderColor: '#bae6fd',
                color: '#0369a1',
                bgcolor: '#f0f9ff',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
              }}
            >
              {exportingMatrix ? 'Generando excel...' : 'Descargar excel'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Modal temporal de aviso: descarga de Excel en desarrollo */}
      <Dialog
        open={excelNoticeOpen}
        onClose={() => setExcelNoticeOpen(false)}
        maxWidth="xs"
        fullWidth
        transitionDuration={0}
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: { xs: 2.5, sm: 3.5 },
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              border: '3px solid #0284c7',
              color: '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              fontSize: '2.2rem',
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            i
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
            En desarrollo
          </Typography>
          <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.6 }}>
            Esta funcionalidad se encuentra actualmente en desarrollo. Disculpen las molestias.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 2, pb: 1 }}>
          <Button
            variant="contained"
            onClick={() => setExcelNoticeOpen(false)}
            onMouseDown={() => setExcelNoticeOpen(false)}
            sx={{
              minWidth: 100,
              borderRadius: '50px',
              bgcolor: '#0284c7',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 0.8,
              '&:hover': { bgcolor: '#0369a1' },
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de aviso de fin de plazo (después del día 20) */}
      <Dialog
        open={deadlineNoticeOpen}
        onClose={handleCloseDeadlineNotice}
        maxWidth="sm"
        fullWidth
        transitionDuration={0}
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: { xs: 2.5, sm: 3.5 },
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: '50%',
              border: '3.5px solid #f97316',
              color: '#f97316',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              fontSize: '2.6rem',
              fontWeight: 400,
              lineHeight: 1,
            }}
          >
            !
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#334155', mb: 2.5, fontSize: '1.75rem' }}>
            Atención!
          </Typography>
          <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.6, mb: 1.5, fontSize: '1.1rem' }}>
            <b>SOLO</b> se pagarán los extras que hayan sido validados por el Responsable antes del <b>día 20 a las 23.59H.</b>
          </Typography>
          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 800, fontSize: '1.2rem' }}>
            El plazo para este mes ha finalizado.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 2, pb: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseDeadlineNotice}
            onMouseDown={handleCloseDeadlineNotice}
            sx={{
              minWidth: 80,
              borderRadius: 1,
              bgcolor: '#3085d6',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              px: 3,
              py: 0.8,
              '&:hover': { bgcolor: '#2563eb' },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

ExtrasView.propTypes = {
  fixedTab: PropTypes.string,
};

ExtrasView.defaultProps = {
  fixedTab: null,
};